import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import FileUploadZone from './components/FileUploadZone';
import ProductPreviewTable from './components/ProductPreviewTable';
import BulkEditPanel from './components/BulkEditPanel';
import PublishingPanel from './components/PublishingPanel';
import ValidationSummary from './components/ValidationSummary';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver/dist/FileSaver';
import Input from '../../components/ui/Input';
import { uploadSessionManager, uploadHistoryManager, fileRecoveryManager } from '../../utils/uploadStorage';
import { useNotification } from '../../components/ui/NotificationProvider';
import { Checkbox } from '../../components/ui/Checkbox';
import mercadoLibreService from '../../services/mercadoLibreService';
import { convertUsdToCopIfNeeded, ensureSpanishFields } from '../../utils/contentSanitizer';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import fileBatchQueue from './fileBatchQueue.cjs';
import bulkEditApplier from './bulkEditApplier.cjs';
import { createPublicationRunId } from '../upload-history/publicationRunHistory.cjs';

const {
  createQueuedBatches,
  completeAndActivateNextBatch,
  getBatchById,
  getPreviewFallbackBatch,
  removeQueuedBatches,
  updateBatchById
} = fileBatchQueue;

const {
  applyQueueBulkEditPresetToBatch,
  createQueueBulkEditPreset
} = bulkEditApplier;

const BulkUpload = () => {
  const navigate = useNavigate();
  const { user, userProfile } = useAuth();
  const { showSuccess, showError, showWarning, showConfirmation, showInfo } = useNotification();
  const [currentStep, setCurrentStep] = useState('upload'); // upload, preview, publish
  const [uploadedFiles, setUploadedFiles] = useState([]);
  // Soporte multi-archivo: cada archivo se procesa en un lote separado (no se mezclan productos)
  const [fileBatches, setFileBatches] = useState([]); // [{id, fileName, fileSize, fileType, products, selectedProducts, omittedInfo, validationResults, isAmazonFile, status}]
  const [activeBatchId, setActiveBatchId] = useState(null);
  const [previewBatchId, setPreviewBatchId] = useState(null);
  const [selectedPendingBatchIds, setSelectedPendingBatchIds] = useState([]);
  const fileBatchesRef = useRef([]);
  const [queueBulkEditPreset, setQueueBulkEditPreset] = useState(null);
  const queueBulkEditPresetRef = useRef(null);
  const [products, setProducts] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const selectedProductsForBulkEdit = useMemo(
    () => products.filter(product => selectedProducts.includes(product?.id)),
    [products, selectedProducts]
  );
  const [isProcessing, setIsProcessing] = useState(false);
  const [isPublishing, setIsPublishingState] = useState(false);
  const setIsPublishing = value => {
    window.__MERCADOBULK_PUBLISH_ACTIVE__ = Boolean(value);
    setIsPublishingState(value);
  };
  const [validationResults, setValidationResults] = useState(null);
  const [publishingProgress, setPublishingProgress] = useState(null);
  const [isPaused, setIsPaused] = useState(false);
  const [shouldStopPublishing, setShouldStopPublishing] = useState(false);

  // Refs para control en tiempo real durante procesos async (evita closures stale)
  const isPausedRef = useRef(false);
  const shouldStopPublishingRef = useRef(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showRecoveryModal, setShowRecoveryModal] = useState(false);
  const [recoveryData, setRecoveryData] = useState(null);
  const [showValidationModal, setShowValidationModal] = useState(false);
  const [validationModalCategory, setValidationModalCategory] = useState(null);
  const [selectedAWSColumns, setSelectedAWSColumns] = useState([]);
  const [showColumnPreview, setShowColumnPreview] = useState(false);
  const [isAmazonFile, setIsAmazonFile] = useState(false);
  const [showColumnSelector, setShowColumnSelector] = useState(false);
  const [amazonDownload, setAmazonDownload] = useState({ keyword: '', category: '', maxResults: 50, isDownloading: false, error: null });
  const [omittedInfo, setOmittedInfo] = useState({ count: 0, details: [] });
  const [showOmittedModal, setShowOmittedModal] = useState(false);
  const [usdToCopRate, setUsdToCopRate] = useState(() => {
    const stored = (() => {
      try {
        return Number(localStorage.getItem('ml_usd_rate'));
      } catch {
        return NaN;
      }
    })();
    return Number.isFinite(stored) && stored > 0
      ? stored
      : null;
  });
  const [usdToCopRateFetchedAt, setUsdToCopRateFetchedAt] = useState(null);
  const [usdToCopRateStatus, setUsdToCopRateStatus] = useState('loading');

  useEffect(() => {
    fileBatchesRef.current = fileBatches;
  }, [fileBatches]);

  useEffect(() => {
    queueBulkEditPresetRef.current = queueBulkEditPreset;
  }, [queueBulkEditPreset]);

  const fetchCurrentUsdToCopRate = async () => {
    setUsdToCopRateStatus('loading');
    try {
      const apiBase = import.meta.env?.VITE_API_BASE_URL || window.location.origin;
      const response = await fetch(`${apiBase}/api/utils/usd-to-cop-rate`, {
        cache: 'no-store'
      });

      if (!response.ok) {
        throw new Error('No fue posible obtener la tasa USD→COP actual. El archivo no fue convertido.');
      }

      const payload = await response.json().catch(() => ({}));
      const rate = Number(payload?.rate);
      if (!Number.isFinite(rate) || rate <= 0) {
        throw new Error('No fue posible obtener la tasa USD→COP actual. El archivo no fue convertido.');
      }

      setUsdToCopRate(rate);
      setUsdToCopRateFetchedAt(payload?.fetchedAt || null);
      setUsdToCopRateStatus('available');
      try {
        localStorage.setItem('ml_usd_rate', String(rate));
      } catch {
        // ignore storage errors
      }

      return rate;
    } catch (error) {
      setUsdToCopRateStatus('unavailable');
      throw error;
    }
  };

  useEffect(() => {
    const fetchRate = async () => {
      try {
        await fetchCurrentUsdToCopRate();
      } catch (err) {
        console.warn('No se pudo actualizar la tasa USD→COP automática:', err);
      }
    };

    fetchRate();
    // La carga inicial es solo informativa; cada archivo vuelve a consultar la tasa.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const hasUsdToCopRate = usdToCopRateStatus === 'available' && Number.isFinite(usdToCopRate) && usdToCopRate > 0;
  const formattedUsdToCopRate = hasUsdToCopRate
    ? new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      currencyDisplay: 'narrowSymbol',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(usdToCopRate)
    : null;
  const formattedUsdToCopFetchedAt = hasUsdToCopRate && usdToCopRateFetchedAt
    ? new Intl.DateTimeFormat('es-CO', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      timeZone: 'America/Bogota'
    }).format(new Date(usdToCopRateFetchedAt))
    : null;

  const getActiveBatch = () => {
    if (!Array.isArray(fileBatches) || fileBatches.length === 0) return null;
    return fileBatches.find(b => b?.id === activeBatchId) || fileBatches[0];
  };

  const getPreviewBatch = () => {
    if (!Array.isArray(fileBatches) || fileBatches.length === 0) return null;
    return getBatchById(fileBatches, previewBatchId) || getActiveBatch() || fileBatches[0];
  };

  const syncViewFromBatch = (batch) => {
    if (!batch) return;
    setProducts(Array.isArray(batch?.products) ? batch.products : []);
    setSelectedProducts(Array.isArray(batch?.selectedProducts) ? batch.selectedProducts : []);
    setOmittedInfo(batch?.omittedInfo || { count: 0, details: [] });
    setValidationResults(batch?.validationResults || null);
    setIsAmazonFile(Boolean(batch?.isAmazonFile));
  };

  const updatePreviewBatch = (updates = {}) => {
    const batchId = previewBatchId || activeBatchId;
    if (!batchId) return;
    updateBatch(batchId, updates);
  };

  const updateBatch = (batchId, updates = {}) => {
    if (!batchId) return;
    setFileBatches(prev => {
      const next = updateBatchById(prev, batchId, {
        ...updates,
        lastUpdated: new Date()?.toISOString()
      });
      fileBatchesRef.current = next;
      return next;
    });
  };

  // Asegurar batch activo para la cola de publicación.
  useEffect(() => {
    if (!fileBatches?.length) return;
    if (!activeBatchId) {
      setActiveBatchId(fileBatches[0]?.id);
      return;
    }
    const exists = fileBatches.some(b => b?.id === activeBatchId);
    if (!exists) {
      setActiveBatchId(fileBatches[0]?.id);
    }
  }, [fileBatches, activeBatchId]);

  // Asegurar que la Vista Previa siempre apunta a un batch existente.
  useEffect(() => {
    if (!fileBatches?.length) {
      if (previewBatchId) setPreviewBatchId(null);
      return;
    }
    if (!getBatchById(fileBatches, previewBatchId)) {
      setPreviewBatchId(activeBatchId || fileBatches[0]?.id);
    }
  }, [fileBatches, previewBatchId, activeBatchId]);

  useEffect(() => {
    const batch = getPreviewBatch();
    if (batch) syncViewFromBatch(batch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewBatchId, fileBatches]);
  
  const handleDownloadFromAmazon = async () => {
    if (!amazonDownload?.keyword?.trim()) {
      setAmazonDownload(prev => ({ ...prev, error: 'Por favor ingresa una palabra clave.' }));
      showError('Por favor ingresa una palabra clave para buscar en Amazon');
      return;
    }
    setAmazonDownload(prev => ({ ...prev, isDownloading: true, error: null }));
    try {
      const apiBase = import.meta.env?.VITE_API_BASE_URL || window.location.origin;
      const response = await fetch(`${apiBase}/api/amazon/download-products`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          keyword: amazonDownload.keyword,
          category: amazonDownload.category,
          maxResults: amazonDownload.maxResults
        })
      });
      const result = await response.json();
      if (result?.success && Array.isArray(result?.products)) {
        const batchId = `amazon_${Date.now()}`;
        const batch = {
          id: batchId,
          fileName: `amazon_${amazonDownload.keyword || 'productos'}_${new Date()?.toISOString()?.split('T')?.[0]}.json`,
          fileSize: 'N/A',
          fileType: 'amazon',
          isAmazonFile: true,
          products: result.products,
          selectedProducts: [],
          omittedInfo: { count: 0, details: [] },
          validationResults: null,
          status: 'active'
        };
        fileBatchesRef.current = [batch];
        setFileBatches([batch]);
        setActiveBatchId(batchId);
        setPreviewBatchId(batchId);
        setSelectedPendingBatchIds([]);
        queueBulkEditPresetRef.current = null;
        setQueueBulkEditPreset(null);
        syncViewFromBatch(batch);
        setCurrentStep('preview');
        showSuccess(`Descargados ${result.products.length} productos desde Amazon`, { title: 'Descarga Masiva Amazon' });
      } else {
        setAmazonDownload(prev => ({ ...prev, error: result?.message || 'Error al descargar productos.' }));
        showError(result?.message || 'Error al descargar productos desde Amazon');
      }
    } catch (error) {
      setAmazonDownload(prev => ({ ...prev, error: 'Error de red o servidor.' }));
      showError('Error de red o servidor al descargar productos desde Amazon');
    } finally {
      setAmazonDownload(prev => ({ ...prev, isDownloading: false }));
    }
  };

  // Configuración de columnas basada en el esquema Apify vigente
    // Definición SOLO con las columnas del JSON de Apify proporcionadas por el usuario
    const apifyColumns = [
      { id: 'asin', label: 'asin' },
      { id: 'originalAsin', label: 'originalAsin' },
      { id: 'título', label: 'título' },
      { id: 'precio', label: 'precio' },
      { id: 'marca', label: 'marca' },
      { id: 'descripción', label: 'descripción' },
      { id: 'Descripción general del producto', label: 'Descripción general del producto' },
      { id: 'características', label: 'características' },
      { id: 'atributos', label: 'atributos' },
      { id: 'atributos de variante', label: 'atributos de variante' },
      { id: 'Detalles de la variante', label: 'Detalles de la variante' },
      { id: 'variantesAsins', label: 'variantesAsins' },
      { id: 'Imágenes de alta resolución', label: 'Imágenes de alta resolución' },
      { id: 'URL de producto no normalizada', label: 'URL de producto no normalizada' },
      { id: 'URL', label: 'URL' },
      { id: 'texto de ubicación', label: 'texto de ubicación' },
      { id: 'Reseñas de la página del producto', label: 'Reseñas de la página del producto' },
      { id: 'Información importante', label: 'Información importante' }
    ];

    const [selectedColumns, setSelectedColumns] = useState(
      Object.fromEntries(apifyColumns.map(col => [col.id, true]))
    );

  // Definición de columnas: esquema Apify en el orden requerido
  const allColumns = [
    { id: 'asin', label: 'asin' },
    { id: 'originalAsin', label: 'originalAsin' },
    { id: 'título', label: 'título' },
    { id: 'precio', label: 'precio' },
    { id: 'marca', label: 'marca' },
    { id: 'descripción', label: 'descripción' },
    { id: 'Descripción general del producto', label: 'Descripción general del producto' },
    { id: 'características', label: 'características' },
    { id: 'atributos', label: 'atributos' },
    { id: 'atributos de variante', label: 'atributos de variante' },
    { id: 'Detalles de la variante', label: 'Detalles de la variante' },
    { id: 'variantesAsins', label: 'variantesAsins' },
    { id: 'Imágenes de alta resolución', label: 'Imágenes de alta resolución' },
    { id: 'URL de producto no normalizada', label: 'URL de producto no normalizada' },
    { id: 'URL', label: 'URL' },
    { id: 'texto de ubicación', label: 'texto de ubicación' },
    { id: 'Reseñas de la página del producto', label: 'Reseñas de la página del producto' },
    { id: 'Información importante', label: 'Información importante' }
  ];
  // Columnas activas confirmadas por el usuario
  // Definición estricta de las columnas requeridas

  // Mock product data for demonstration
  const mockProducts = [
    {
      id: 1,
      title: "iPhone 14 Pro Max 256GB Space Black - Nuevo en Caja Sellada con Garantía Oficial Apple",
      price: 899999,
      category: "MLA1051",
      description: "iPhone 14 Pro Max con pantalla Super Retina XDR de 6.7 pulgadas, chip A16 Bionic, sistema de cámaras Pro con teleobjetivo 3x, Modo Acción para videos estables y detección de choques.",
      condition: "new",
      stock: 15,
      images: ["https://images.unsplash.com/photo-1592750475338-74b7b21085ab?w=400"],
      originalTitle: "iPhone 14 Pro Max 256GB Space Black - Nuevo en Caja Sellada con Garantía Oficial Apple Argentina"
    },
    {
      id: 2,
      title: "Samsung Galaxy S23 Ultra 512GB Phantom Black - Liberado",
      price: 749999,
      category: "MLA1051",
      description: "Samsung Galaxy S23 Ultra con S Pen integrado, cámara de 200MP, pantalla Dynamic AMOLED 2X de 6.8 pulgadas y batería de 5000mAh.",
      condition: "new",
      stock: 8,
      images: ["https://images.unsplash.com/photo-1610945265064-0e34e5519bbf?w=400"]
    },
    {
      id: 3,
      title: "MacBook Pro 14 M2 Pro 16GB RAM 512GB SSD - Space Gray",
      price: 1299999,
      category: "MLA1648",
      description: "MacBook Pro de 14 pulgadas con chip M2 Pro, pantalla Liquid Retina XDR, 16GB de memoria unificada y SSD de 512GB.",
      condition: "new",
      stock: 5,
      images: ["https://images.unsplash.com/photo-1541807084-5c52b6b3adef?w=400"]
    },
    {
      id: 4,
      title: "PlayStation 5 Console Standard Edition - Nueva Sellada",
      price: 549999,
      category: "MLA1132",
      description: "Consola PlayStation 5 Standard Edition con lector de discos, control DualSense inalámbrico y juegos en 4K.",
      condition: "new",
      stock: 12,
      images: ["https://images.unsplash.com/photo-1606813907291-d86efa9b94db?w=400"]
    },
    {
      id: 5,
      title: "Smart TV Samsung 65 4K UHD QLED Q80B - Tizen OS",
      price: 899999,
      category: "MLA1367",
      description: "Smart TV Samsung QLED de 65 pulgadas con resolución 4K UHD, tecnología Quantum Dot, HDR10+ y sistema operativo Tizen.",
      condition: "new",
      stock: 7,
      images: ["https://images.unsplash.com/photo-1593359677879-a4bb92f829d1?w=400"]
    }
  ];

  const steps = [
    { id: 'upload', label: 'Cargar Archivo', icon: 'Upload' },
    { id: 'preview', label: 'Vista Previa', icon: 'Eye' },
    { id: 'publish', label: 'Publicar', icon: 'Rocket' }
  ];

  // Validación deshabilitada: retornar resultados exitosos
  const performValidation = () => {
    const total = products?.length || 0;
    const result = {
      total,
      valid: total,
      warnings: 0,
      errors: 0,
      details: []
    };
    setValidationResults(result);
    return result;
  };

  // Descarga de plantilla con el número dinámico de columnas actualmente activas
  const handleDownloadTemplate = () => {
    // Get only selected columns for template
    const selectedColumnsList = allColumns?.filter(col => selectedColumns?.[col?.id]);
    
    // Crear plantilla con las columnas Apify seleccionadas
    const makeRow = (idx) => {
      const row = {};
      const sample = idx === 0
        ? {
            asin: 'B0EXAMPLE1', originalAsin: 'B0ORIG1', 'título': 'Ejemplo Producto 1', precio: '199900',
            marca: 'Genérica',
            'descripción': 'Descripción de ejemplo en español.', 'Descripción general del producto': 'Punto 1 | Punto 2',
            'características': 'Ligero | Resistente', atributos: 'Color: Negro | Tamaño: M', 'atributos de variante': 'Color | Tamaño',
            'Detalles de la variante': 'Rojo; Azul', variantesAsins: 'B0VAR1, B0VAR2',
            'Imágenes de alta resolución': 'https://ejemplo.com/img1.jpg, https://ejemplo.com/img2.jpg',
            'URL de producto no normalizada': 'https://amazon.com/p/abc',
            URL: 'https://amazon.com/p/abc?ref=1',
            'texto de ubicación': 'Bogotá, Colombia',
            'Reseñas de la página del producto': 'Excelente | Recomendado', 'Información importante': 'Leer instrucciones'
          }
        : {
            asin: 'B0EXAMPLE2', originalAsin: 'B0ORIG2', 'título': 'Ejemplo Producto 2', precio: '99900',
            marca: 'MarcaX',
            'descripción': 'Otro producto de ejemplo.', 'Descripción general del producto': 'Info 1 | Info 2',
            'características': 'Compacto | Eficiente', atributos: 'Color: Blanco', 'atributos de variante': 'Color',
            'Detalles de la variante': 'Blanco', variantesAsins: 'B0VAR3',
            'Imágenes de alta resolución': 'https://ejemplo.com/p2-1.jpg',
            'URL de producto no normalizada': 'https://amazon.com/p/xyz', URL: 'https://amazon.com/p/xyz?ref=2',
            'texto de ubicación': 'Medellín, Colombia',
            'Reseñas de la página del producto': 'Muy bueno',
            'Información importante': 'Garantía 6 meses'
          };
      apifyColumns.forEach(col => {
        if (selectedColumns?.[col.id]) {
          row[col.label] = sample[col.label] ?? '';
        }
      });
      return row;
    };

    const templateData = [makeRow(0), makeRow(1)];

    // Create workbook and worksheet
    const wb = XLSX?.utils?.book_new();
    const ws = XLSX?.utils?.json_to_sheet(templateData);

    // Set column widths based on content
    const columnWidths = [];
    Object?.keys(templateData?.[0] || {})?.forEach(key => {
      if (key === 'Título' || key === 'Descripción' || key === 'Título Asignado') {
        columnWidths?.push({ wch: 50 });
      } else if (key === 'Resultado Observaciones') {
        columnWidths?.push({ wch: 30 });
      } else if (key?.includes('Imagen')) {
        columnWidths?.push({ wch: 35 });
      } else if (key === 'Precio' || key === 'Stock' || key === 'Largo') {
        columnWidths?.push({ wch: 12 });
      } else {
        columnWidths?.push({ wch: 20 });
      }
    });

    ws['!cols'] = columnWidths;

    XLSX?.utils?.book_append_sheet(wb, ws, 'Plantilla Productos');

    // Generate file and download
    const excelBuffer = XLSX?.write(wb, { bookType: 'xlsx', type: 'array' });
    const data = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const selectedCount = Object?.values(selectedColumns)?.filter(Boolean)?.length;
    saveAs(data, `plantilla-productos-${allColumns?.length}-columnas-${new Date()?.toISOString()?.split('T')?.[0]}.xlsx`);

    showSuccess(`Plantilla Excel con las ${allColumns?.length} columnas específicas descargada correctamente`, {
      title: 'Descarga Completa',
      duration: 5000
    });
  };

  // Add download error report functionality - move before usage
  const downloadErrorReport = () => {
    if (!publishingProgress?.errors?.length) return;

    const errorData = publishingProgress?.errors?.map(error => ({
      'Producto': error?.productTitle || 'N/A',
      'Error': error?.message || error,
      'Timestamp': error?.timestamp || new Date()?.toISOString(),
      'Reintentar': error?.retryable ? 'Sí' : 'No'
    }));

    // Create workbook and worksheet
    const wb = XLSX?.utils?.book_new();
    const ws = XLSX?.utils?.json_to_sheet(errorData);

    // Set column widths
    ws['!cols'] = [
      { wch: 50 }, // Producto
      { wch: 30 }, // Error
      { wch: 20 }, // Timestamp
      { wch: 12 }  // Reintentar
    ];

    XLSX?.utils?.book_append_sheet(wb, ws, 'Errores Publicación');

    // Generate file and download
    const excelBuffer = XLSX?.write(wb, { bookType: 'xlsx', type: 'array' });
    const data = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    saveAs(data, `errores-publicacion-${new Date()?.toISOString()?.split('T')?.[0]}.xlsx`);

    showSuccess('Reporte de errores descargado correctamente', {
      title: 'Descarga Completa'
    });
  };

  // Download report of omitted products due to prohibited terms
  const downloadOmittedReport = () => {
    if (!omittedInfo?.details?.length) return;

    const rows = omittedInfo.details.map(d => ({
      'Producto': d?.product || 'N/A',
      'Coincidencias': Array.isArray(d?.matches) ? d.matches.join(', ') : ''
    }));

    const wb = XLSX?.utils?.book_new();
    const ws = XLSX?.utils?.json_to_sheet(rows);
    ws['!cols'] = [
      { wch: 60 }, // Producto
      { wch: 40 }  // Coincidencias
    ];
    XLSX?.utils?.book_append_sheet(wb, ws, 'Productos Omitidos');
    const excelBuffer = XLSX?.write(wb, { bookType: 'xlsx', type: 'array' });
    const data = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    saveAs(data, `productos-omitidos-${new Date()?.toISOString()?.split('T')?.[0]}.xlsx`);

    showSuccess('Reporte de productos omitidos descargado', { title: 'Descarga Completa' });
  };

  // Add error analysis handler
  const handleErrorAnalysis = (analysis) => {
    console.log('Análisis de errores:', analysis);
    
    if (analysis?.recommendations?.length > 0) {
      showInfo(`Se encontraron ${analysis?.productsWithErrors} productos con errores y ${analysis?.productsWithWarnings} con advertencias.`, {
        title: 'Análisis Completado',
        duration: 6000,
        actions: analysis?.recommendations?.slice(0, 2)?.map((rec, index) => ({
          label: `Solución ${index + 1}`,
          onClick: () => applySolution(rec),
          variant: 'outline'
        }))
      });
    } else {
      showSuccess('¡Excelente! No se encontraron problemas críticos en los productos.', {
        title: 'Sin Errores Detectados'
      });
    }
  };

  // Add solution application function
  const applySolution = (recommendation) => {
    if (recommendation?.includes('títulos faltantes')) {
      // Auto-fix missing titles
      const updatedProducts = products?.map(product => ({
        ...product,
        title: product?.title || `Producto ${product?.id} - ${product?.category || 'Sin categoría'}`
      }));
      setProducts(updatedProducts);
      showSuccess('Títulos faltantes completados automáticamente');
    } else if (recommendation?.includes('precios inválidos')) {
      // Fix invalid prices
      const updatedProducts = products?.map(product => ({
        ...product,
        price: (!product?.price || product?.price <= 0) ? 999 : product?.price
      }));
      setProducts(updatedProducts);
      showSuccess('Precios inválidos corregidos automáticamente');
    } else if (recommendation?.includes('categorías')) {
      // Suggest category assignment
      showInfo('Se recomienda asignar categorías manualmente usando la edición masiva', {
        title: 'Asignación de Categorías',
        actions: [{
          label: 'Abrir Edición Masiva',
          onClick: () => setSelectedProducts(products?.filter(p => !p?.category)?.map(p => p?.id)),
          variant: 'default'
        }]
      });
    }
  };

  const restoreUploadSessionState = (session) => {
    if (!session) return false;

    const savedProducts = Array.isArray(session?.products)
      ? session.products
      : [];

    let restoredBatches = Array.isArray(session?.fileBatches)
      ? session.fileBatches
      : [];

    // Compatibilidad con sesiones antiguas que solo tenían products.
    if (!restoredBatches.length && savedProducts.length) {
      const firstFile = Array.isArray(session?.uploadedFiles)
        ? session.uploadedFiles[0]
        : null;

      const recoveredBatchId =
        session?.activeBatchId ||
        `recovered-${Date.now()}`;

      const detectedCategory =
        savedProducts.find(product => product?.category_id)?.category_id ||
        null;

      restoredBatches = [{
        id: recoveredBatchId,
        fileName: firstFile?.name || 'Sesión recuperada',
        fileSize: '',
        fileType: firstFile?.name?.includes('.')
          ? firstFile.name.split('.').pop().toLowerCase()
          : '',
        isAmazonFile: Boolean(session?.isAmazonFile),
        products: savedProducts,
        selectedProducts: Array.isArray(session?.selectedProducts)
          ? session.selectedProducts
          : [],
        omittedInfo: session?.omittedInfo || { count: 0, details: [] },
        validationResults: session?.validationResults || null,
        detectedCategory,
        detectedCategoryId: detectedCategory,
        status: 'active'
      }];
    }

    if (!restoredBatches.length) return false;

    fileBatchesRef.current = restoredBatches;
    setFileBatches(restoredBatches);

    const restoredActiveId =
      restoredBatches.some(batch => batch?.id === session?.activeBatchId)
        ? session.activeBatchId
        : restoredBatches[0]?.id;

    const restoredPreviewId =
      restoredBatches.some(batch => batch?.id === session?.previewBatchId)
        ? session.previewBatchId
        : restoredActiveId;

    setActiveBatchId(restoredActiveId);
    setPreviewBatchId(restoredPreviewId);

    const previewBatch =
      restoredBatches.find(batch => batch?.id === restoredPreviewId) ||
      restoredBatches[0];

    syncViewFromBatch(previewBatch);

    if (Array.isArray(session?.uploadedFiles)) {
      setUploadedFiles(session.uploadedFiles);
    }

    setSelectedPendingBatchIds(
      Array.isArray(session?.selectedPendingBatchIds)
        ? session.selectedPendingBatchIds
        : []
    );

    if (session?.queueBulkEditPreset !== undefined) {
      queueBulkEditPresetRef.current = session.queueBulkEditPreset || null;
      setQueueBulkEditPreset(session.queueBulkEditPreset || null);
    }

    if (session?.currentStep) {
      setCurrentStep(session.currentStep);
    } else {
      setCurrentStep('preview');
    }

    return true;
  };

  // Al volver desde otra sección del menú, restaurar inmediatamente
  // desde memoria. El modal queda reservado para una recarga real.
  useEffect(() => {
    let cancelled = false;

    const initializeRecovery = async () => {
      try {
        const runtimeSession =
          typeof window !== 'undefined'
            ? window.__MERCADOBULK_UPLOAD_RUNTIME__
            : null;

        if (runtimeSession && restoreUploadSessionState(runtimeSession)) {
          return;
        }

        const fullSession =
          await fileRecoveryManager?.loadFullSession?.();

        if (cancelled) return;

        if (fullSession) {
          setRecoveryData({
            session: fullSession,
            source: 'indexeddb'
          });
          setShowRecoveryModal(true);
          return;
        }

        // Compatibilidad temporal con sesiones viejas de localStorage.
        if (fileRecoveryManager?.hasRecoverableData()) {
          const recovery = fileRecoveryManager?.getRecoveryData();
          setRecoveryData(recovery);
          setShowRecoveryModal(true);
        }
      } catch (error) {
        console.error('Error inicializando recuperación:', error);
      }
    };

    initializeRecovery();

    return () => {
      cancelled = true;
    };
    // Solo debe ejecutarse al montar la pantalla.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Abrir modal de detalles de validación desde el resumen
  const handleOpenValidationDetails = (categoryType) => {
    setValidationModalCategory(categoryType || 'all');
    setShowValidationModal(true);
  };

  const handleCloseValidationDetails = () => {
    setShowValidationModal(false);
    setValidationModalCategory(null);
  };

  useEffect(() => {
    // Simulate validation when products are loaded
    if (products?.length > 0) {
      performValidation();
      // Auto-save when products change
      uploadSessionManager?.autoSave({
        products,
        selectedProducts,
        currentStep,
        validationResults
      }, 'preview');
    }
  }, [products]);

  // Auto-save legacy metadata plus the COMPLETE live session.
  useEffect(() => {
    if (
      uploadedFiles?.length > 0 ||
      products?.length > 0 ||
      fileBatches?.length > 0
    ) {
      const uploadedFileMetadata = uploadedFiles?.map(file => ({
        name: file?.name,
        size: file?.size,
        type: file?.type,
        lastModified: file?.lastModified
      })) || [];

      // Se mantiene el guardado antiguo solo como compatibilidad.
      uploadSessionManager?.saveSession({
        currentStep,
        uploadedFiles: uploadedFileMetadata,
        products,
        selectedProducts,
        validationResults
      });

      // Esta es la sesión real completa.
      const completeSession = {
        currentStep,
        uploadedFiles: uploadedFileMetadata,
        products,
        selectedProducts,
        validationResults,
        fileBatches,
        activeBatchId,
        previewBatchId,
        selectedPendingBatchIds,
        queueBulkEditPreset,
        omittedInfo,
        isAmazonFile,
        timestamp: new Date().toISOString()
      };

      try {
        if (typeof window !== 'undefined') {
          window.__MERCADOBULK_UPLOAD_RUNTIME__ = completeSession;
        }
      } catch {}

      fileRecoveryManager
        ?.saveFullSession?.(completeSession)
        ?.catch?.(error => {
          console.error('Error guardando sesión completa:', error);
        });
    }
  }, [
    currentStep,
    uploadedFiles,
    products,
    selectedProducts,
    validationResults,
    fileBatches,
    activeBatchId,
    previewBatchId,
    selectedPendingBatchIds,
    queueBulkEditPreset,
    omittedInfo,
    isAmazonFile
  ]);

  // Apply business rules to processed products
  const applyAmazonBusinessRules = (products) => {
    return products?.map(product => ({
      ...product,
      // Set 12-day availability automatically
      availabilityDays: 12,
      availableUntil: new Date(Date.now() + (12 * 24 * 60 * 60 * 1000))?.toISOString()?.split('T')?.[0],
    }));
  };

  // Handle column selection changes
  const handleColumnChange = (columnId, checked) => {
    setSelectedColumns(prev => ({
      ...prev,
      [columnId]: checked
    }));
  };

  // Handle select/deselect all columns
  const handleSelectAllColumns = (checked) => {
    const newSelection = {};
    allColumns?.forEach(col => {
      newSelection[col?.id] = checked;
    });
    setSelectedColumns(newSelection);
  };

  const getSourceDescription = (source = {}) => {
    const descriptionObject = source?.description;
    const candidates = [
      typeof descriptionObject === 'object' ? descriptionObject?.plain_text : descriptionObject,
      source?.['Descripción'],
      source?.['descripción'],
      source?.Description,
      source?.descripcion,
      source?.['Descripción general del producto']
    ];
    const description = candidates.find(value => typeof value === 'string' && value.trim());
    return description?.trim() || 'Producto disponible. Consulta las características indicadas en la publicación.';
  };

  const buildBatchFromFile = async (file, batchIndex = 0) => {
    if (!file) throw new Error('Archivo inválido');
    // Enforce tamaño máximo de 10MB
    if (file?.size && file.size > 10 * 1024 * 1024) {
      throw new Error(`El archivo "${file?.name}" supera el tamaño máximo permitido de 10MB.`);
    }

    const currentRate = await fetchCurrentUsdToCopRate();
    const batchId = `${Date.now()}_${batchIndex}_${Math.random().toString(16).slice(2)}`;
    const fileSizeLabel = file?.size ? `${(file.size / 1024 / 1024).toFixed(1)} MB` : 'N/A';

    const isJson = file?.type === 'application/json' || file?.name?.toLowerCase?.().endsWith('.json');
    if (isJson) {
      const text = await file.text();
      let apifyData = [];
      try {
        apifyData = JSON.parse(text);
      } catch {
        throw new Error(`El archivo JSON "${file?.name}" no es válido.`);
      }
      if (!Array.isArray(apifyData) || apifyData.length === 0) {
        throw new Error(`El archivo JSON "${file?.name}" no contiene productos.`);
      }

      const fileCategoryId = (() => {
        const match = String(file?.name || '').match(/\b(MCO\d+)\b/i);
        return match?.[1]?.toUpperCase?.() || '';
      })();

      const processApifyProduct = async (item, idx) => {
        const base = (item && typeof item === 'object') ? item : {};
        // Preservar TODAS las claves del JSON original (incluyendo atributos extra)
        // y mantener un id interno para selección/edición.
        const product = { ...base, id: idx + 1 };
        // Mapeo manual campo a campo y alias para la UI
        product['asin'] = base.asin || product['asin'] || '';
        product['originalAsin'] = base.originalAsin || product['originalAsin'] || '';
        // Título y alias
        product['título'] = base.title || base['título'] || product['título'] || '';
        product['titulo'] = product['título'];
        product['title'] = product['título'];
        product['category_id'] = base.category_id || base.categoria_id || base.category || base.categoria || product['category_id'] || fileCategoryId || '';
        // Precio y alias (convertir a COP si corresponde)
        const rawPrice = base.price?.value ?? base['precio'] ?? base.price ?? '';
        let priceNum = Number(rawPrice);
        if (typeof rawPrice === 'string') {
          const cleaned = rawPrice.replace(/[^0-9.,]/g, '').replace(/,/g, '.');
          priceNum = parseFloat(cleaned);
        }
        const shouldConvert = (mercadoLibreService?.siteId || 'MCO') === 'MCO';
        const conv = shouldConvert
          ? convertUsdToCopIfNeeded(rawPrice, priceNum, currentRate)
          : { converted: false, value: priceNum };
        product['precio'] = conv.value || 0;
        product['price'] = product['precio'];
        if (conv.converted && Number.isFinite(priceNum) && priceNum > 0) {
          product['amazonPriceUsd'] = priceNum;
        } else if (Number.isFinite(Number(base.amazonPriceUsd)) && Number(base.amazonPriceUsd) > 0) {
          product['amazonPriceUsd'] = Number(base.amazonPriceUsd);
        }
        // Marca y alias
        product['marca'] = base.brand || base['marca'] || product['marca'] || '';
        product['brand'] = product['marca'];
        // Descripción y alias
        const descRaw = getSourceDescription(base);
        const titleRaw = base.title || base['título'] || '';
        let prodSan = {
          descripcion: descRaw,
          description: descRaw,
          titulo: titleRaw,
          title: titleRaw
        };
        // Asegurar español si sitio es MCO (título y descripción)
        if ((mercadoLibreService?.siteId || 'MCO') === 'MCO') {
          prodSan = await ensureSpanishFields(prodSan);
        }
        product['descripción'] = prodSan.descripcion ?? descRaw;
        product['descripcion'] = prodSan.descripcion ?? descRaw;
        product['description'] = prodSan.description ?? descRaw;
        product['título'] = prodSan.titulo ?? titleRaw;
        product['titulo'] = prodSan.titulo ?? titleRaw;
        product['title'] = prodSan.title ?? titleRaw;

        // Campos Apify comunes
        product['Descripción general del producto'] = Array.isArray(item.productOverview) ? item.productOverview.map(e => e.value).join(' | ') : (item['Descripción general del producto'] || '');

        // Conservar también la estructura original de Amazon.
        // El motor de precios necesita las claves originales como
        // Item Weight / Package Weight / Product Dimensions.
        if (Array.isArray(item.productOverview)) {
          product.productOverview = item.productOverview.map(entry => (
            entry && typeof entry === 'object'
              ? { ...entry }
              : entry
          ));
        }
        product['características'] = Array.isArray(item.features) ? item.features.join(' | ') : (item['características'] || '');
        product['atributos'] = Array.isArray(item.attributes) ? item.attributes.map(e => `${e.key}: ${e.value}`).join(' | ') : (item['atributos'] || '');
        product['atributos de variante'] = Array.isArray(item.variantAttributes) ? item.variantAttributes.map(e => `${e.key}: ${e.value}`).join(' | ') : (item['atributos de variante'] || '');
        product['Detalles de la variante'] = Array.isArray(item.variantDetails) ? item.variantDetails.map(e => e.name).join(' | ') : (item['Detalles de la variante'] || '');
        product['variantesAsins'] = Array.isArray(item.variantAsins) ? item.variantAsins.join(', ') : (item['variantesAsins'] || '');
        product['Imágenes de alta resolución'] = Array.isArray(item.highResolutionImages) ? item.highResolutionImages.join(', ') : (item['Imágenes de alta resolución'] || '');
        product['URL de producto no normalizada'] = item.unNormalizedProductUrl || item['URL de producto no normalizada'] || '';
        product['URL'] = item.url || item['URL'] || '';
        product['texto de ubicación'] = item.locationText || item['texto de ubicación'] || '';
        product['Reseñas de la página del producto'] = Array.isArray(item.productPageReviews) ? item.productPageReviews.map(e => e.reviewTitle).join(' | ') : (item['Reseñas de la página del producto'] || '');
        product['Información importante'] = item.importantInformation || item['Información importante'] || '';

        // Normalizar a español más campos textuales comunes si el sitio es MCO
        if ((mercadoLibreService?.siteId || 'MCO') === 'MCO') {
          const textFields = [
            'Descripción general del producto',
            'características',
            'atributos',
            'atributos de variante',
            'Detalles de la variante',
            'Reseñas de la página del producto',
            'Información importante',
            'texto de ubicación'
          ];
          const translated = await ensureSpanishFields(product, textFields);
          textFields.forEach(key => { if (translated?.[key] !== undefined) product[key] = translated[key]; });
        }
        return product;
      };

      const processedProducts = await Promise.all(apifyData.map(processApifyProduct));
      return {
        id: batchId,
        fileName: file?.name || `archivo_${batchIndex + 1}.json`,
        fileSize: fileSizeLabel,
        fileType: 'json',
        isAmazonFile: false,
        products: processedProducts,
        selectedProducts: [],
        omittedInfo: { count: 0, details: [] },
        validationResults: null,
        detectedCategory: fileCategoryId || processedProducts.find(product => product?.category_id)?.category_id || null,
        detectedCategoryId: fileCategoryId || processedProducts.find(product => product?.category_id)?.category_id || null,
        status: 'pending'
      };
    }

    // Excel/CSV
    const arrayBuffer = await file?.arrayBuffer();
    const workbook = XLSX?.read(arrayBuffer, { type: 'array' });
    const sheetName = workbook?.SheetNames?.[0];
    const worksheet = workbook?.Sheets?.[sheetName];
    const jsonData = XLSX?.utils?.sheet_to_json(worksheet);
    const limited = (jsonData?.length > 10000) ? jsonData.slice(0, 10000) : jsonData;
    if (jsonData?.length > 10000) {
      showWarning(`El archivo "${file?.name}" contiene ${jsonData?.length} productos. Se procesarán los primeros 10,000.`, {
        title: 'Límite de Productos Alcanzado',
        duration: 6000
      });
    }
    const result = await processProductData(limited, file, { returnOnly: true, currentRate });
    const processedProducts = Array.isArray(result?.processedProducts) ? result.processedProducts : [];
    const fileCategoryId = String(file?.name || '').match(/\b(MCO\d+)\b/i)?.[1]?.toUpperCase?.() || '';
    return {
      id: batchId,
      fileName: file?.name || `archivo_${batchIndex + 1}`,
      fileSize: fileSizeLabel,
      fileType: (file?.name?.split('.')?.pop() || '').toLowerCase(),
      isAmazonFile: false,
      products: processedProducts,
      selectedProducts: [],
      omittedInfo: { count: 0, details: [] },
      validationResults: null,
      detectedCategory: fileCategoryId || processedProducts.find(product => product?.category_id)?.category_id || null,
      detectedCategoryId: fileCategoryId || processedProducts.find(product => product?.category_id)?.category_id || null,
      status: 'pending'
    };
  };

  // Procesamiento de archivo mejorado que respeta la configuración de columnas activa
  const handleFileSelect = async (files) => {
    setIsProcessing(true);

    try {
      // MCO_ZIP_JSON_UPLOAD_V1
      // Descomprimir ZIP localmente y mantener JSON sueltos.
      const selectedFiles = Array.isArray(files)
        ? files
        : Array.from(files || []);

      const list = [];
      const zipErrors = [];

      const hasZip = selectedFiles.some(file =>
        /\.zip$/i.test(String(file?.name || ''))
      );

      let JSZip = null;

      if (hasZip) {
        const zipModule = await import('jszip');
        JSZip = zipModule.default || zipModule;
      }

      for (const file of selectedFiles) {
        if (!file) continue;

        if (!/\.zip$/i.test(String(file.name || ''))) {
          list.push(file);
          continue;
        }

        try {
          const zip = await JSZip.loadAsync(
            await file.arrayBuffer()
          );

          const entries = Object.values(zip.files).filter(entry => {
            if (entry.dir) return false;

            const fullName = String(entry.name || '')
              .replace(/\\/g, '/');

            const fileName = fullName.split('/').pop() || '';

            return /\.json$/i.test(fileName) &&
              !fullName.split('/').includes('__MACOSX') &&
              !fileName.startsWith('._');
          });

          if (!entries.length) {
            zipErrors.push({
              name: file.name,
              error: 'ZIP sin archivos JSON'
            });
            continue;
          }

          for (const entry of entries) {
            const originalName = String(entry.name)
              .replace(/\\/g, '/')
              .split('/')
              .pop();

            const content = await entry.async('uint8array');

            const jsonFile = new File(
              [content],
              originalName,
              {
                type: 'application/json',
                lastModified: file.lastModified || Date.now()
              }
            );

            list.push(jsonFile);
          }
        } catch (error) {
          zipErrors.push({
            name: file.name,
            error: error?.message || 'ZIP no procesable'
          });
        }
      }

      // Solamente JSON extraídos y archivos sueltos.
      setUploadedFiles(list);

      uploadSessionManager?.autoSave({
        files: list.map(f => ({
          name: f?.name,
          size: f?.size,
          type: f?.type
        })),
        timestamp: new Date()?.toISOString()
      }, 'upload');

      if (!list.length) {
        showError('No se encontraron archivos procesables.');
        return;
      }

      if (list.length === 0) {
        showError('No se seleccionaron archivos.');
        return;
      }

      const batches = [];
      const failedFiles = [...zipErrors];

      for (let i = 0; i < list.length; i++) {
        const file = list[i];
        try {
          const batch = await buildBatchFromFile(file, i);
          if (batch && Array.isArray(batch.products) && batch.products.length > 0) {
            batches.push(batch);
          } else {
            failedFiles.push({ name: file?.name, error: 'Archivo sin productos procesables' });
          }
        } catch (err) {
          const error = err?.message || String(err);
          failedFiles.push({ name: file?.name, error });
          if (error.includes('No fue posible obtener la tasa USD→COP actual')) {
            showError(`No fue posible obtener la tasa USD→COP actual. El archivo "${file?.name}" no fue convertido.`, {
              title: 'Tasa de cambio no disponible',
              persistent: true
            });
          }
        }
      }

      if (batches.length === 0) {
        showError('No se pudo procesar ningún archivo. Verifica el formato.');
        return;
      }

      const queuedBatches = createQueuedBatches(batches);
      fileBatchesRef.current = queuedBatches;
      setFileBatches(queuedBatches);
      setActiveBatchId(queuedBatches[0]?.id);
      setPreviewBatchId(queuedBatches[0]?.id);
      setSelectedPendingBatchIds([]);
      queueBulkEditPresetRef.current = null;
      setQueueBulkEditPreset(null);
      syncViewFromBatch(queuedBatches[0]);
      setCurrentStep('preview');

      const totalProducts = batches.reduce((acc, b) => acc + (Array.isArray(b?.products) ? b.products.length : 0), 0);
      showSuccess(`Archivos procesados: ${batches.length}. Total productos: ${totalProducts}.`, {
        title: 'Carga múltiple lista',
        duration: 6000
      });

      if (failedFiles.length > 0) {
        showWarning(`Algunos archivos no se procesaron: ${failedFiles.slice(0, 3).map(f => f.name).join(', ')}${failedFiles.length > 3 ? ` y ${failedFiles.length - 3} más` : ''}`, {
          title: 'Archivos omitidos',
          duration: 8000
        });
      }
    } catch (error) {
      console.error('Error procesando archivo:', error);
      showError(`Error al procesar el archivo. Verifica el formato y que contenga las ${allColumns?.length} columnas específicas.`, {
        title: 'Error de Procesamiento',
        persistent: true,
        actions: [{
          label: `Descargar Plantilla ${allColumns?.length} Columnas`,
          onClick: handleDownloadTemplate,
          variant: 'outline',
          icon: 'Download'
        }]
      });
    } finally {
      setIsProcessing(false);
    }
  };

  // Separate function to process product data with proper column mapping
  async function processProductData(jsonData, file, options = {}) {
    // Get file headers
    const headers = Object?.keys(jsonData?.[0] || {});
    
  // Verificar las columnas específicas contra los headers del archivo
    const matchedColumns = [];
    const missingColumns = [];
    
    allColumns?.forEach(col => {
      const matchingHeader = headers?.find(header => 
        header?.toLowerCase()?.replace(/\s+/g, '') === col?.label?.toLowerCase()?.replace(/\s+/g, '') ||
        header === col?.label
      );
      if (matchingHeader) {
        matchedColumns?.push(col?.label);
      } else {
        missingColumns?.push(col?.label);
      }
    });

    // Procesar todas las columnas que existan en el archivo según configuración
    const shouldConvert = (mercadoLibreService?.siteId || 'MCO') === 'MCO';
    const currentRate = Number(options?.currentRate);
    if (shouldConvert && (!Number.isFinite(currentRate) || currentRate <= 0)) {
      throw new Error('No existe una tasa USD→COP válida para convertir el precio');
    }
  let processedProducts = await Promise.all((jsonData || [])?.map(async (row, index) => {
      const addImage = (collection, rawUrl) => {
        if (!rawUrl) return;
        const normalized = String(rawUrl).trim();
        if (!normalized || !normalized.startsWith('http')) return;
        if (!collection.includes(normalized)) {
          collection.push(normalized);
        }
      };

      const sourceDescription = getSourceDescription(row);
      const product = {
        id: index + 1,
        // Always include core fields for functionality
        title: row?.['Título'] || row?.['titulo'] || '',
        titulo: row?.['Título'] || row?.['titulo'] || '', // Keep both for compatibility
        // Precio: convertir USD→COP si detectamos USD o magnitud baja
        price: (() => {
          const raw = row?.['Precio'] || row?.['precio'] || 0;
          const cleaned = typeof raw === 'string' ? raw.replace(/[^0-9.,A-Za-z$]/g, '').replace(/,/g, '.') : raw;
          const num = typeof cleaned === 'string' ? parseFloat(cleaned) : Number(cleaned);
          const conv = shouldConvert
            ? convertUsdToCopIfNeeded(raw, num, currentRate)
            : { converted: false, value: num };
          return conv.value || 0;
        })(),
        precio: 0, // se sincroniza abajo con price
        amazonPriceUsd: (() => {
          const explicit = Number(row?.amazonPriceUsd ?? row?.amazon_price_usd);
          if (Number.isFinite(explicit) && explicit > 0) return explicit;

          const raw = row?.['Precio'] || row?.['precio'] || 0;
          const cleaned = typeof raw === 'string'
            ? raw.replace(/[^0-9.,A-Za-z$]/g, '').replace(/,/g, '.')
            : raw;
          const num = typeof cleaned === 'string' ? parseFloat(cleaned) : Number(cleaned);

          if (!Number.isFinite(num) || num <= 0) return null;

          const conv = shouldConvert
            ? convertUsdToCopIfNeeded(raw, num, currentRate)
            : { converted: false };

          return conv.converted ? num : null;
        })(),
        category: row?.['Categoría'] || row?.['categoria'] || '',
        categoria: row?.['Categoría'] || row?.['categoria'] || '', // Keep both for compatibility
        description: sourceDescription,
        descripcion: sourceDescription, // Keep both for compatibility
        // Set stock to 0 for MercadoLibre uploads
        stock: parseInt(row?.['Stock'] || 0),
        images: []
      };
      product.precio = product.price;

      // Preservar texto original (solo traducir si el sitio lo requiere)
      let prodSan = {
        titulo: product.titulo,
        title: product.title,
        descripcion: product.descripcion,
        description: product.description
      };
      if ((mercadoLibreService?.siteId || 'MCO') === 'MCO') {
        prodSan = await ensureSpanishFields(prodSan);
      }
      product.titulo = prodSan.titulo ?? product.titulo;
      product.title = prodSan.title ?? product.title;
      product.descripcion = prodSan.descripcion ?? product.descripcion;
      product.description = prodSan.description ?? product.description;

      // Procesar columnas Apify (imágenes de alta resolución)
      const highRes = row?.['Imágenes de alta resolución'];
      if (typeof highRes === 'string' && highRes.trim()) {
        highRes
          .split(/[,;\n\r\t|]+/)
          .map(s => s.trim())
          .filter(Boolean)
          .forEach(url => addImage(product.images, url));
      } else if (Array.isArray(highRes)) {
        highRes.forEach(url => addImage(product.images, url));
      }
      if (product.images.length) {
        product['Imágenes de alta resolución'] = [...product.images];
      }

      // Copiar y traducir otros campos textuales comunes si existen en el Excel/CSV
      const extraTextFields = [
        'Descripción general del producto',
        'características',
        'atributos',
        'atributos de variante',
        'Detalles de la variante',
        'Reseñas de la página del producto',
        'Información importante',
        'texto de ubicación'
      ];
      extraTextFields.forEach(k => { if (row?.[k] != null) product[k] = row[k]; });
      if ((mercadoLibreService?.siteId || 'MCO') === 'MCO') {
        const translated = await ensureSpanishFields(product, extraTextFields);
        extraTextFields.forEach(key => { if (translated?.[key] !== undefined) product[key] = translated[key]; });
      }

      // Preservar TODAS las columnas del archivo Excel/CSV sin romper los campos canónicos
      return { ...(row || {}), ...product };
    }));

    if (options?.returnOnly) {
      return {
        processedProducts,
        matchedColumns,
        missingColumns
      };
    }

    // Conservar todos los productos procesados y delegar validaciones a MercadoLibre
    setOmittedInfo({ count: 0, details: [] });
    setProducts(processedProducts);
    updatePreviewBatch({
      products: processedProducts,
      selectedProducts: [],
      omittedInfo: { count: 0, details: [] }
    });
    setCurrentStep('preview');

    // Enhanced success message with capacity info
  let message = `Archivo procesado correctamente. ${processedProducts?.length} productos cargados con las ${allColumns?.length} columnas específicas.`;
    
    if (matchedColumns?.length > 0) {
      message += ` Columnas encontradas: ${matchedColumns?.length}/${allColumns?.length}.`;
    }
    
    if (processedProducts?.length >= 2000) {
      message += ` ⚡ Procesamiento optimizado para gran volumen de productos.`;
    }
    
    if (missingColumns?.length > 0) {
      showWarning(`Algunas columnas no fueron encontradas en el archivo: ${missingColumns?.slice(0, 5)?.join(', ')}${missingColumns?.length > 5 ? ` y ${missingColumns?.length - 5} más` : ''}`, {
        title: `${missingColumns?.length} Columnas Faltantes`,
        duration: 8000
      });
    }

    showSuccess(message, {
      title: `Archivo Procesado - ${processedProducts?.length} Productos`,
      duration: 6000,
      actions: [{
        label: 'Ver Vista Previa',
        onClick: () => setCurrentStep('preview'),
        variant: 'default'
      }]
    });
  }

  // Enhanced bulk update handler
  const handleBulkUpdate = async (changes) => {
    const currentBatch = getPreviewBatch();
    const detectedCategory = currentBatch?.detectedCategoryId || currentBatch?.detectedCategory || '';
    const selectedCategory = changes?.category || '';
    const batchOverride = selectedCategory && selectedCategory !== detectedCategory
      ? selectedCategory
      : currentBatch?.categoryOverride;
    const queuePreset = createQueueBulkEditPreset(changes);
    const queueSnapshot = fileBatchesRef.current;
    let preparedBatches = (queueSnapshot || []).map(batch => {
      const batchWithOverride = batch?.id === currentBatch?.id && batchOverride
        ? { ...batch, categoryOverride: batchOverride, bulkPresetApplied: false }
        : { ...batch, bulkPresetApplied: false };
      return applyQueueBulkEditPresetToBatch(batchWithOverride, queuePreset, { applyAllProducts: true });
    });

    // MB_GUARDAR_ERRORES_PRECIO_V2
    // Aplicar la exclusión después de recuperar los atributos MCO.
    const failedById = new Map(
      (changes?.pricingErrors || [])
        .filter(error => error?.productId != null)
        .map(error => [
          String(error.productId),
          error.reason || "Error de cálculo"
        ])
    );

    const previousById = new Map(
      (currentBatch?.products || []).map(product => [
        String(product?.id),
        product
      ])
    );

    preparedBatches = preparedBatches.map(batch => {
      if (batch?.id !== currentBatch?.id) {
        return batch;
      }

      return {
        ...batch,
        products: (batch?.products || []).map(product => {
          const id = String(product?.id);
          const error = failedById.get(id);

          if (error) {
            const original = previousById.get(id) || product;

            return {
              ...product,
              price: original.price,
              precio: original.precio,
              mercadoBulkBloqueadoPorPrecio: true,
              mercadoBulkMotivoBloqueo: error
            };
          }

          const asin = String(
            product?.asin ||
            product?.ASIN ||
            product?.amazon_asin ||
            product?.originalAsin ||
            ""
          ).trim().toUpperCase();

          const calculated = Object.prototype.hasOwnProperty.call(
            changes?.pricingFinalPricesByAsin || {},
            asin
          );

          if (calculated) {
            return {
              ...product,
              mercadoBulkBloqueadoPorPrecio: false,
              mercadoBulkMotivoBloqueo: null
            };
          }

          return product;
        })
      };
    });

    fileBatchesRef.current = preparedBatches;
    setFileBatches(preparedBatches);
    const preparedPreviewBatch = getBatchById(preparedBatches, currentBatch?.id);
    if (preparedPreviewBatch) syncViewFromBatch(preparedPreviewBatch);
    queueBulkEditPresetRef.current = queuePreset;
    setQueueBulkEditPreset(queuePreset);

    const updatedCount = preparedBatches.reduce((total, batch) => total + (batch?.products?.length || 0), 0);
    showSuccess(`${updatedCount} productos actualizados en ${preparedBatches.length} archivo(s)`, {
      title: 'Edición Masiva Completada'
    });
  };

  // Add product editing functionality  
  const handleProductUpdate = (productId) => {
    const product = products?.find(p => p?.id === productId);
    if (product) {
      setEditingProduct(product);
      setShowEditModal(true);
    }
  };

  const handleSaveProduct = async (updatedProduct) => {
    const originalProduct = (products || []).find(
      p => p?.id === updatedProduct?.id
    );

    if (!originalProduct) {
      showError('No se encontró el producto que intentas editar.');
      return;
    }

    const precioCop = Number(updatedProduct?.price);

    if (!Number.isFinite(precioCop) || precioCop < 0) {
      showError('Ingresa un precio válido en pesos colombianos.');
      return;
    }

    const precioAnteriorCop = Number(originalProduct?.price);
    const cambioPrecio = precioCop !== precioAnteriorCop;

    const productToSave = {
      ...updatedProduct,
      price: precioCop
    };

    if (cambioPrecio) {
      productToSave.precio = precioCop;

      if (precioCop > 0) {
        let tasa;

        try {
          tasa = Number(await fetchCurrentUsdToCopRate());
        } catch (error) {
          showError('No se pudo consultar la tasa USD a COP. No se guardó el cambio.');
          return;
        }

        if (!Number.isFinite(tasa) || tasa <= 0) {
          showError('La tasa USD a COP no es válida. No se guardó el cambio.');
          return;
        }

        const equivalenteUsd = precioCop / tasa;
        productToSave.amazonPriceUsd = equivalenteUsd;

        if (Object.prototype.hasOwnProperty.call(productToSave, 'amazon_price_usd')) {
          productToSave.amazon_price_usd = equivalenteUsd;
        }
      } else {
        productToSave.amazonPriceUsd = null;

        if (Object.prototype.hasOwnProperty.call(productToSave, 'amazon_price_usd')) {
          productToSave.amazon_price_usd = null;
        }
      }
    }

    const nextProducts = (products || []).map(p =>
      p?.id === updatedProduct?.id ? productToSave : p
    );
    setProducts(nextProducts);
    updatePreviewBatch({ products: nextProducts });
    setShowEditModal(false);
    setEditingProduct(null);

    showSuccess('Producto actualizado correctamente', {
      title: 'Producto Guardado'
    });
  };

  // Delete a single product from the list
  const handleDeleteProduct = (productId) => {
    const nextProducts = (products || []).filter(
      p => p?.id !== productId
    );
    const nextSelectedProducts = (selectedProducts || []).filter(
      id => id !== productId
    );

    setProducts(nextProducts);
    setSelectedProducts(nextSelectedProducts);
    updatePreviewBatch({
      products: nextProducts,
      selectedProducts: nextSelectedProducts
    });
    if (editingProduct?.id === productId) {
      setShowEditModal(false);
      setEditingProduct(null);
    }
    showInfo('Producto eliminado de la lista de carga', { title: 'Producto Eliminado' });
  };

  // Request delete with app confirmation dialog (no navegador)
  const handleRequestDeleteProduct = (productId) => {
    const product = products?.find(p => p?.id === productId);
    showConfirmation({
      title: 'Eliminar Producto',
      message: `¿Deseas eliminar "${product?.titulo || product?.title || 'este producto'}" de la carga?`,
      confirmText: 'Sí, eliminar',
      cancelText: 'Cancelar',
      type: 'danger',
      onConfirm: () => handleDeleteProduct(productId)
    });
  };

  // REAL PUBLISHING FUNCTION - Replace simulation with actual MercadoLibre API calls
  const handleStartPublishing = async (settings, batchOverride = null, publicationRunId = null) => {
    console.log('🚀 Iniciando proceso de publicación...');

    const activeBatch = batchOverride || getActiveBatch();
    if (activeBatch?.categoryPending) {
      showError(`El archivo "${activeBatch?.fileName || 'actual'}" tiene categoría pendiente de revisión.`);
      return;
    }
    const batchProducts = activeBatch?.products || [];
    const batchSelection = activeBatch?.selectedProducts || [];
    // MB_GUARDAR_ERRORES_PRECIO_V2
    // Esta protección también cubre el siguiente lote automático.
    const originalSelection = batchSelection?.length > 0
      ? batchProducts?.filter(product => batchSelection?.includes(product?.id))
      : batchProducts;

    const excludedProducts = (originalSelection || []).filter(product => {
      if (
        product?.mercadoBulkBloqueadoPorPrecio === true ||
        product?.mercadoBulkPriceNeedsReview === true
      ) {
        return true;
      }

      const price = Number(
        product?.price ?? product?.precio
      );

      if (!Number.isFinite(price) || price <= 0) {
        return true;
      }

      const asin = String(
        product?.asin ||
        product?.ASIN ||
        product?.amazon_asin ||
        product?.originalAsin ||
        ""
      ).trim();

      if (asin) {
        const usd = Number(
          product?.amazonPriceUsd ??
          product?.amazon_price_usd
        );

        if (!Number.isFinite(usd) || usd <= 0) {
          return true;
        }
      }

      return false;
    });

    const excludedSet = new Set(excludedProducts);

    const baseSelection = (originalSelection || []).filter(
      product => !excludedSet.has(product)
    );

    if (excludedProducts.length > 0) {
      console.warn(
        "[MercadoBulk] Excluidos antes de publicar:",
        excludedProducts.map(product => ({
          asin: product?.asin || product?.ASIN || "",
          precio: product?.price ?? product?.precio,
          motivo:
            product?.mercadoBulkMotivoBloqueo ||
            "Precio no verificable"
        }))
      );

      showWarning(
        `${excludedProducts.length} producto(s) excluidos ` +
        "por problemas de precio. No se enviarán a Mercado Libre."
      );
    }

    if (!baseSelection || baseSelection.length === 0) {
      showError('No hay productos disponibles para publicar.');
      return;
    }

    const currentPublicationRunId = publicationRunId || createPublicationRunId();

    if (!batchSelection || batchSelection.length === 0) {
      const next = baseSelection.map(p => p?.id);
      setSelectedProducts(next);
      updateBatch(activeBatch?.id || activeBatchId, { selectedProducts: next });
    }

    const productsToPublish = await Promise.all(baseSelection.map(async (product) => {
      if (!product?.category_id && !product?.categoria && !product?.category) {
        const detectedCategory = await mercadoLibreService?.detectCategoryId(product?.titulo || product?.title || '');
        if (detectedCategory) {
          return { ...product, category_id: detectedCategory };
        }
        return { ...product, category_id: mercadoLibreService?.defaultCategoryId };
      }
      return product;
    }));

    const fileName = activeBatch?.fileName || uploadedFiles?.[0]?.name || `bulk_upload_${Date.now()}.xlsx`;
    
    // Cambiar al paso de publicación PRIMERO
    setCurrentStep('publish');
    
    // Pequeña pausa para permitir que la UI se actualice
    await new Promise(resolve => setTimeout(resolve, 100));
    
    // AHORA iniciar el proceso
    setIsPublishing(true);

    // Resetear flags de control
    setIsPaused(false);
    isPausedRef.current = false;
    setShouldStopPublishing(false);
    shouldStopPublishingRef.current = false;
    
    // Inicializar progreso
    setPublishingProgress({
      completed: 0,
      total: productsToPublish.length,
      percentage: 0,
      currentProduct: null,
      successful: 0,
      failed: 0,
      errors: [],
      isPaused: false
    });
    
    // Create initial history entry (incluye details para dashboard/actividad)
    const totalItems = productsToPublish?.length || 0;
    const { data: sessionData } = await supabase?.auth?.getSession?.() || {};
    const sessionEmail = sessionData?.session?.user?.email;
    const rememberedEmail = (() => {
      try {
        return localStorage?.getItem('mlbp:lastUserEmail') || null;
      } catch (storageError) {
        console.warn('No se pudo recuperar el correo recordado del usuario:', storageError?.message);
        return null;
      }
    })();

    const uploaderEmail = user?.email || userProfile?.email || sessionEmail || rememberedEmail || 'Usuario desconocido';

    const historyEntry = uploadHistoryManager?.addToHistory({
      type: 'bulk_upload',
      title: `Carga de ${totalItems} productos`,
      description: `0 exitosos, 0 fallidos`,
      fileName,
      totalProducts: totalItems,
      successCount: 0,
      failedCount: 0,
      status: "in-progress",
      duration: "En progreso...",
      fileSize: activeBatch?.fileSize || (uploadedFiles?.[0]?.size ? `${(uploadedFiles?.[0]?.size / 1024 / 1024)?.toFixed(1)} MB` : 'N/A'),
      uploadedBy: uploaderEmail,
      errorSummary: [],
      details: { total: totalItems, successful: 0, failed: 0 },
      publicationRunId: currentPublicationRunId
    });

    showInfo(`🚀 Iniciando publicación REAL de ${productsToPublish?.length} productos en MercadoLibre...`, {
      title: 'Publicación Real Iniciada',
      duration: 3000
    });

    // Ejecutar el proceso de publicación de forma asíncrona
    setTimeout(() => {
      processPublishing(productsToPublish, settings, historyEntry, activeBatch?.id || activeBatchId, currentPublicationRunId);
    }, 500);
  };

  // Función separada para procesar la publicación
  const processPublishing = async (productsToPublish, settings, historyEntry, batchIdAtStart, publicationRunId) => {

    let completed = 0;
    let successful = 0;
    let failed = 0;
    const total = productsToPublish?.length;
    const startTime = Date.now();
    let errors = [];

    // Guardar detalle de resultados para que el Historial (botón "Ver") muestre productos.
    // Para evitar exceder el límite de localStorage, solo guardamos TODOS los productos en lotes pequeños.
    const MAX_PRODUCTS_TO_PERSIST = 500;
    const persistMode = total <= MAX_PRODUCTS_TO_PERSIST ? 'all' : 'failedOnly';
    const persistedProducts = [];
    let persistTruncated = false;

    const safeStr = (v) => {
      const s = (v === null || v === undefined) ? '' : String(v);
      return s.length > 500 ? s.slice(0, 500) : s;
    };

    const buildProductResult = (product, status, result, errMsg) => {
      const title = product?.titulo || product?.title || '';
      const sku = product?.sku || product?.asin || product?.originalAsin || product?.userProductId || product?.id || '';
      const price = Number(product?.price ?? product?.precio ?? 0) || 0;
      return {
        id: product?.id ?? sku ?? title,
        sku: safeStr(sku),
        title: safeStr(title),
        price,
        status,
        mlId: result?.mercadoLibreId || result?.mlId || '',
        mlUrl: result?.permalink || result?.mlUrl || '',
        error: safeStr(errMsg || ''),
        publishedAt: new Date()?.toISOString()
      };
    };
    
    // SMART RATE LIMITING - Individual product processing with adaptive delays
  const maxRetriesSetting = settings?.autoRetry ? Math.max(1, parseInt(settings?.maxRetries || 1)) : 1;
  const publishMode = settings?.publishMode || 'draft';
  let initialDelay = (settings?.delayBetweenProducts || 7) * 1000; // Start with 7 seconds (more conservative)
    let dynamicDelay = initialDelay;
    let consecutiveErrors = 0;
    
    console.log(`🛡️ Starting with smart rate limiting - initial delay: ${initialDelay/1000}s`);
    
    // Reglas opcionales de corte automático
    const abortOnConsecutiveErrors = Boolean(settings?.abortOnConsecutiveErrors);
    const maxConsecutiveErrors = Math.max(1, parseInt(settings?.maxConsecutiveErrors ?? 10));
    const abortOnFailureRate = Boolean(settings?.abortOnFailureRate);
    const maxFailureRate = (() => {
      const v = Number(settings?.maxFailureRate ?? 0.8);
      if (!Number.isFinite(v)) return 0.8;
      return Math.min(0.99, Math.max(0.1, v));
    })();
    const minSamplesForFailureRate = Math.max(1, parseInt(settings?.minSamplesForFailureRate ?? 10));

    const finalizeEarlyExit = (status, reason) => {
      if (historyEntry) {
        uploadHistoryManager?.updateHistoryEntry(historyEntry?.id, {
          successCount: successful,
          failedCount: failed,
          status,
          duration: `${Math.round((Date.now() - startTime) / 1000)} seg`,
          details: { total, successful, failed },
          description: reason ? `${successful} exitosos, ${failed} fallidos · ${reason}` : `${successful} exitosos, ${failed} fallidos`,
          products: persistedProducts,
          productsMode: persistMode,
          productsTruncated: persistTruncated
        });
      }
      setPublishingProgress(prev => ({
        ...(prev || {}),
        successful,
        failed,
        isPaused: false,
        waitingMessage: null,
        cancelled: status === 'cancelled',
        cancellationReason: reason || null
      }));
      setIsPublishing(false);
    };

    // Process each product individually with SMART RATE LIMITING
    for (let i = 0; i < productsToPublish?.length; i++) {
      const product = productsToPublish[i];
      
      // Check for pause before each product
      while (isPausedRef.current) {
        if (shouldStopPublishingRef.current) break;
        await new Promise(resolve => setTimeout(resolve, 500));
      }

      // If user requested hard stop, finalize gracefully
      if (shouldStopPublishingRef.current) {
        finalizeEarlyExit('cancelled', 'Cancelado por el usuario');
        return;
      }
      
      console.log(`📦 Publishing product ${i + 1}/${total}: ${product?.titulo || product?.title}`);
      console.log(`📂 Category: ${product?.categoria || product?.category || 'No category'}`);
      console.log(`⏱️ Current delay: ${Math.round(dynamicDelay / 1000)}s (consecutive errors: ${consecutiveErrors})`);
      
      try {
        // Intentos automáticos por producto
        let attempts = 0;
        let published = false;
        let lastErrorMessage = '';
        let lastResult = null;
        while (attempts < maxRetriesSetting && !published) {
          attempts++;
          const result = await mercadoLibreService?.publishProduct(product);
          if (result?.success) {
            // Si modo borrador: pausar inmediatamente
            if (publishMode === 'draft' && result?.mercadoLibreId) {
              try {
                await mercadoLibreService?.pauseItem(result.mercadoLibreId);
                console.log(`⏸️ Item pausado para revisión (draft): ${result.mercadoLibreId}`);
              } catch (e) {
                console.warn('No se pudo pausar el item recién publicado:', e?.message || e);
              }
            }
            successful++;
            consecutiveErrors = 0;
            dynamicDelay = Math.max(3000, dynamicDelay * 0.95);
            console.log(`✅ Product "${product?.titulo || product?.title}" published successfully (attempt ${attempts}/${maxRetriesSetting})`);
            published = true;
            lastResult = result;
            break;
          } else {
            lastErrorMessage = result?.error?.message || result?.error || 'Unknown error';
            const retryable = !!result?.error?.retryable;
            lastResult = result;
            if (!retryable) break;
            // pequeño backoff entre intentos
            await new Promise(resolve => setTimeout(resolve, Math.min(5000, 1000 * attempts)));
          }
        }

        if (!published) {
          failed++;
          consecutiveErrors++;
          // Si es un error de autenticación, notificar y detener sin llenar la lista de errores
          const msg = (lastErrorMessage || '').toString();
          const isAuthError = msg.toLowerCase().includes('no estás autenticado') || msg.toLowerCase().includes('no esta autenticado') || msg.includes('401') || msg.toLowerCase().includes('unauthorized');
          if (isAuthError) {
            showError('No estás autenticado con MercadoLibre. Inicia sesión primero.', {
              title: 'API Desconectada'
            });
            // Actualizar progreso y detener el proceso
            setPublishingProgress(prev => ({
              ...prev,
              failed,
              consecutiveErrors,
              waitingMessage: null,
              isPaused: true
            }));
            // Actualizar historial como parcial y salir del procesamiento
            if (historyEntry) {
              uploadHistoryManager?.updateHistoryEntry(historyEntry?.id, {
                successCount: successful,
                failedCount: failed,
                status: 'partial',
                duration: `${Math.round((Date.now() - startTime) / 1000)} seg`,
                details: { total, successful, failed },
                description: `${successful} exitosos, ${failed} fallidos`
              });
            }
            setIsPublishing(false);
            return; // detener todo el flujo de publicación
          }
          errors.push(`${product?.titulo || product?.title}: ${lastErrorMessage || 'Error desconocido'}`);
          // Ajuste de delay si parece rate limit
          if (msg.includes('429') || msg.toLowerCase().includes('rate limit') || msg.includes('Too Many Requests') || msg.toLowerCase().includes('quota')) {
            console.log(`🚨 RATE LIMIT DETECTED - Increasing delay significantly`);
            dynamicDelay = Math.min(60000, dynamicDelay * 4);
          } else if (consecutiveErrors >= 3) {
            dynamicDelay = Math.min(30000, dynamicDelay * 2);
            console.log(`⚠️ Multiple consecutive errors - increasing delay`);
          }
          console.error(`❌ Failed to publish "${product?.titulo || product?.title}":`, lastErrorMessage);
        }

        // Cortes automáticos opcionales
        if (abortOnConsecutiveErrors && consecutiveErrors >= maxConsecutiveErrors) {
          shouldStopPublishingRef.current = true;
          setShouldStopPublishing(true);
          finalizeEarlyExit('cancelled', `Cancelado automáticamente: ${consecutiveErrors} errores consecutivos`);
          return;
        }
        const processedCount = i + 1;
        if (abortOnFailureRate && processedCount >= minSamplesForFailureRate) {
          const rate = failed / processedCount;
          if (rate >= maxFailureRate) {
            shouldStopPublishingRef.current = true;
            setShouldStopPublishing(true);
            finalizeEarlyExit('cancelled', `Cancelado automáticamente: tasa de fallos ${(rate * 100).toFixed(0)}%`);
            return;
          }
        }

        // Persistir resultado del producto para el historial
        const status = published ? 'success' : 'failed';
        if (persistMode === 'all' || status === 'failed') {
          persistedProducts.push(buildProductResult(product, status, lastResult, published ? '' : lastErrorMessage));
        } else {
          persistTruncated = true;
        }

        // Guardar periódicamente para que el modal pueda mostrar datos en caliente
        if (historyEntry && (i % 5 === 0 || i === productsToPublish.length - 1)) {
          uploadHistoryManager?.updateHistoryEntry(historyEntry?.id, {
            products: persistedProducts,
            productsMode: persistMode,
            productsTruncated: persistTruncated
          });
        }

      } catch (error) {
        failed++;
        consecutiveErrors++;
        const errorMessage = error?.message || 'Network error';
        errors.push(`${product?.titulo || product?.title}: ${errorMessage}`);

        // Persistir fallo para el historial
        if (historyEntry) {
          persistedProducts.push(buildProductResult(product, 'failed', null, errorMessage));
          persistTruncated = persistTruncated || (persistMode !== 'all');
          if (i % 5 === 0 || i === productsToPublish.length - 1) {
            uploadHistoryManager?.updateHistoryEntry(historyEntry?.id, {
              products: persistedProducts,
              productsMode: persistMode,
              productsTruncated: persistTruncated
            });
          }
        }
        
        // Network errors might also indicate rate limiting
        if (errorMessage.includes('429') || 
            errorMessage.includes('timeout') ||
            errorMessage.includes('ECONNRESET')) {
          console.log(`🚨 NETWORK/RATE LIMIT ERROR - Increasing delay`);
          dynamicDelay = Math.min(45000, dynamicDelay * 3);
        } else if (consecutiveErrors >= 3) {
          dynamicDelay = Math.min(20000, dynamicDelay * 1.5);
        }
        
        console.error(`❌ Critical error publishing "${product?.titulo || product?.title}":`, error);
      }
      
      // Update individual progress
      completed = i + 1;
      
      setPublishingProgress({
        completed,
        total,
        percentage: Math.round((completed / total) * 100),
        successful,
        failed,
        eta: `${Math.max(0, Math.ceil((total - completed) * dynamicDelay / 60000))} min`,
        errors: errors || [],
        currentProduct: product?.titulo || product?.title,
        currentDelay: Math.round(dynamicDelay / 1000), // Show current delay in UI
        consecutiveErrors: consecutiveErrors
      });

      // Update history entry with progress (sincroniza también details)
      if (historyEntry) {
        uploadHistoryManager?.updateHistoryEntry(historyEntry?.id, {
          successCount: successful,
          failedCount: failed,
          status: completed === total ? (failed === 0 ? "completed" : "partial") : "in-progress",
          duration: completed === total ? `${Math.round((Date.now() - startTime) / 1000)} seg` : "En progreso...",
          errorSummary: errors || [],
          details: { total, successful, failed },
          description: `${successful} exitosos, ${failed} fallidos`
        });
      }
      
      // Smart delay between products (except for last product)
      if (i < productsToPublish?.length - 1) {
        // Extra cooldown for too many consecutive errors
        if (consecutiveErrors >= 5) {
          console.log(`🆘 TOO MANY CONSECUTIVE ERRORS - Adding 60s cooldown period`);
          await new Promise(resolve => setTimeout(resolve, 60000));
          consecutiveErrors = Math.floor(consecutiveErrors / 2); // Reduce but don't reset completely
          dynamicDelay = Math.max(initialDelay, dynamicDelay * 0.8); // Reduce delay after cooldown
        }
        
        console.log(`⏳ Smart waiting ${Math.round(dynamicDelay / 1000)}s before next product...`);
        
        // Responsive delay that respects pause and shows progress
        let delayRemaining = dynamicDelay;
        const delayStep = 1000; // Check every second
        const startDelayTime = Date.now();
        
        while (delayRemaining > 0) {
          if (isPausedRef.current) {
            // If paused, wait until unpaused
            while (isPausedRef.current) {
              if (shouldStopPublishingRef.current) break;
              await new Promise(resolve => setTimeout(resolve, 500));
            }
          }
          // If user requested hard stop during delay, exit now
          if (shouldStopPublishingRef.current) {
            finalizeEarlyExit('cancelled', 'Cancelado por el usuario');
            return;
          }
          
          const currentDelay = Math.min(delayStep, delayRemaining);
          await new Promise(resolve => setTimeout(resolve, currentDelay));
          delayRemaining -= currentDelay;
          
          // Update progress to show waiting time
          const waitedTime = Date.now() - startDelayTime;
          const waitProgress = Math.round((waitedTime / dynamicDelay) * 100);
          
          setPublishingProgress(prev => ({
            ...prev,
            waitingProgress: waitProgress,
            waitingMessage: `Esperando ${Math.ceil(delayRemaining / 1000)}s (rate limiting)...`
          }));
        }
        
        // Clear waiting message
        setPublishingProgress(prev => ({
          ...prev,
          waitingProgress: 0,
          waitingMessage: null
        }));
      }
    }

    // Final history update (refleja métricas finales en details)
    if (historyEntry) {
      uploadHistoryManager?.updateHistoryEntry(historyEntry?.id, {
        successCount: successful,
        failedCount: failed,
        status: failed === 0 ? "completed" : "partial",
        duration: `${Math.round((Date.now() - startTime) / 1000)} seg`,
        errorSummary: errors || [],
        details: { total, successful, failed },
        description: `${successful} exitosos, ${failed} fallidos`,
        products: persistedProducts,
        productsMode: persistMode,
        productsTruncated: persistTruncated
      });
      try {
        localStorage.setItem('mercadolibre_last_sync', new Date().toISOString());
        localStorage.setItem('dashboard_force_reload', Date.now().toString());
      } catch {}
    }

    setIsPublishing(false);

    const batchesWithSummary = (fileBatchesRef.current || []).map(batch =>
      batch?.id === batchIdAtStart
        ? {
            ...batch,
            publishSummary: {
              successful,
              failed,
              status: failed === 0 ? 'completed' : 'partial',
              historyEntryId: historyEntry?.id || null,
              finishedAt: new Date()?.toISOString()
            }
          }
        : batch
    );
    const queueTransition = completeAndActivateNextBatch(batchesWithSummary, batchIdAtStart);
    const preparedNextBatch = applyQueueBulkEditPresetToBatch(
      queueTransition.nextBatch,
      queueBulkEditPresetRef.current
    );
    const nextQueueBatches = preparedNextBatch === queueTransition.nextBatch
      ? queueTransition.batches
      : queueTransition.batches.map(batch => batch?.id === preparedNextBatch?.id ? preparedNextBatch : batch);
    fileBatchesRef.current = nextQueueBatches;
    setFileBatches(nextQueueBatches);

    if (preparedNextBatch) {
      setPublishingProgress(null);
      setEditingProduct(null);
      setShowEditModal(false);
      setActiveBatchId(preparedNextBatch.id);
      setPreviewBatchId(preparedNextBatch.id);
      syncViewFromBatch(preparedNextBatch);
      setCurrentStep('publish');
      setTimeout(() => {
        handleStartPublishing(settings, preparedNextBatch, publicationRunId);
      }, 0);
      return;
    }

    // Show completion notification with real results
    if (failed === 0) {
      showSuccess(`🎉 ¡Publicación REAL completada exitosamente! ${successful} productos publicados en MercadoLibre.`, {
        title: 'Publicación Real Exitosa',
        duration: 6000,
        actions: [
          {
            label: 'Ver Historial',
            onClick: () => navigate('/upload-history'),
            variant: 'default',
            icon: 'History'
          }
        ]
      });
    } else {
      showWarning(`⚠️ Publicación completada con errores. ${successful} exitosos, ${failed} fallidos en MercadoLibre.`, {
        title: 'Publicación Parcial',
        duration: 8000,
        actions: [
          {
            label: 'Ver Errores',
            onClick: downloadErrorReport,
            variant: 'outline',
            icon: 'Download'
          },
          {
            label: 'Ver Historial',
            onClick: () => navigate('/upload-history'),
            variant: 'default',
            icon: 'History'
          }
        ]
      });
    }
    
    // Clear session data after successful completion
    if (successful > 0) {
      fileRecoveryManager?.clearRecoveryData();
    }
    
    setTimeout(() => {
      navigate('/upload-history');
    }, 3000);
  };

  // Pause publishing functionality
  const handlePausePublishing = () => {
    setIsPaused(true);
    isPausedRef.current = true;
    setPublishingProgress(prev => ({
      ...prev,
      isPaused: true
    }));
    showInfo('📤 Publicación pausada. Presiona "Reanudar" para continuar.', {
      title: 'Publicación Pausada'
    });
  };

  // Resume publishing functionality  
  const handleResumePublishing = () => {
    setIsPaused(false);
    isPausedRef.current = false;
    setPublishingProgress(prev => ({
      ...(prev || {}),
      isPaused: false,
      waitingMessage:
        prev?.waitingMessage === 'Publicación pausada'
          ? null
          : prev?.waitingMessage
    }));
    showInfo('▶️ Publicación reanudada.', {
      title: 'Publicación Reanudada'
    });
  };

  const handleCancelPublishing = () => {
    if (!isPublishing) return;
    showConfirmation({
      title: 'Cancelar publicación',
      message: 'Esto detendrá la publicación inmediatamente. Lo ya publicado quedará publicado. ¿Deseas continuar?',
      confirmText: 'Sí, cancelar',
      cancelText: 'No',
      type: 'danger',
      onConfirm: () => {
        setShouldStopPublishing(true);
        shouldStopPublishingRef.current = true;
        // si estaba pausado, liberar para que el loop detecte el stop
        setIsPaused(false);
        isPausedRef.current = false;
        setPublishingProgress(prev => ({
          ...(prev || {}),
          waitingMessage: null,
          cancellationRequested: true
        }));
        showInfo('Cancelación solicitada. Deteniendo publicación...', { title: 'Cancelando' });
      }
    });
  };

  // Updated retry functionality with real API calls
  const retryFailedProducts = async (failedProductIds = []) => {
    if (!publishingProgress?.errors?.length) return;
    
    const productsToRetry = failedProductIds?.length > 0 
      ? products?.filter(p => failedProductIds?.includes(p?.id))
      : products?.filter(p => 
          publishingProgress?.errors?.some(error => 
            error?.productId === p?.id || error?.includes?.(p?.title)
          )
        );

    if (productsToRetry?.length === 0) return;

    setIsPublishing(true);
    
    showInfo(`🔄 Reintentando publicación REAL de ${productsToRetry?.length} productos en MercadoLibre...`, {
      title: 'Reintentando Publicación Real'
    });
    
    let retrySuccessful = 0;
    let retryFailed = 0;
    const retryErrors = [];

    for (const product of productsToRetry) {
      const result = await mercadoLibreService?.publishProduct(product);
      
      if (result?.success) {
        retrySuccessful++;
        // Remove from errors list
        setPublishingProgress(prev => ({
          ...prev,
          errors: prev?.errors?.filter(err => 
            err?.productId !== product?.id && !err?.includes?.(product?.title)
          ),
          successful: prev?.successful + 1,
          failed: prev?.failed - 1
        }));
      } else {
        retryFailed++;
        retryErrors?.push(result?.error);
      }

      // Small delay between retries
      await new Promise(resolve => setTimeout(resolve, 1000));
    }

    setIsPublishing(false);
    
    if (retrySuccessful > 0) {
      showSuccess(`✅ ${retrySuccessful} productos reintentados exitosamente en MercadoLibre`, {
        title: 'Reintento Exitoso'
      });
    }
    
    if (retryFailed > 0) {
      showWarning(`⚠️ ${retryFailed} productos siguen fallando después del reintento`, {
        title: 'Reintento Parcial'
      });
    }
  };

  const handleRecoverData = () => {
    if (recoveryData?.session) {
      const restored =
        restoreUploadSessionState(recoveryData.session);

      if (restored) {
        showSuccess('Datos de sesión recuperados correctamente', {
          title: 'Sesión Restaurada'
        });
      } else {
        showError('No se pudo reconstruir la sesión guardada');
      }
    }

    // Recuperar NO debe borrar la sesión.
    setShowRecoveryModal(false);
  };

  const handleDiscardRecovery = () => {
    setShowRecoveryModal(false);
    setRecoveryData(null);

    fileRecoveryManager?.clearRecoveryData();

    setUploadedFiles([]);
    setProducts([]);
    setSelectedProducts([]);
    setFileBatches([]);
    fileBatchesRef.current = [];
    setActiveBatchId(null);
    setPreviewBatchId(null);
    setSelectedPendingBatchIds([]);
    queueBulkEditPresetRef.current = null;
    setQueueBulkEditPreset(null);
    setValidationResults(null);
    setOmittedInfo({ count: 0, details: [] });
    setCurrentStep('upload');

    showInfo('Iniciando nueva sesión', {
      title: 'Nueva Sesión'
    });
  };

  const handleBulkSelect = (productIds, action) => {
    // productIds puede ser array (varios) o un solo id
    if (action === 'selectAll') {
      const next = Array.isArray(productIds) ? productIds : [];
      setSelectedProducts(next);
      updatePreviewBatch({ selectedProducts: next });
      return;
    }
    if (action === 'deselectAll') {
      setSelectedProducts([]);
      updatePreviewBatch({ selectedProducts: [] });
      return;
    }
    if (action === 'select') {
      const ids = Array.isArray(productIds) ? productIds : [productIds];
      setSelectedProducts(prev => {
        const next = Array.from(new Set([...(prev || []), ...ids]));
        updatePreviewBatch({ selectedProducts: next });
        return next;
      });
      return;
    }
    if (action === 'deselect') {
      const ids = Array.isArray(productIds) ? productIds : [productIds];
      setSelectedProducts(prev => {
        const next = (prev || []).filter(id => !ids.includes(id));
        updatePreviewBatch({ selectedProducts: next });
        return next;
      });
      return;
    }
    // Fallback: si no viene action, mantener compatibilidad anterior
    if (Array.isArray(productIds)) {
      setSelectedProducts(productIds);
      updatePreviewBatch({ selectedProducts: productIds });
    } else {
      setSelectedProducts(prev => {
        const next = prev?.includes(productIds)
          ? prev?.filter(id => id !== productIds)
          : [...(prev || []), productIds];
        updatePreviewBatch({ selectedProducts: next });
        return next;
      });
    }
  };

  const handleDeleteSelected = () => {
    if (!selectedProducts?.length) return;
    showConfirmation({
      title: `Eliminar ${selectedProducts.length} producto(s)` ,
      message: '¿Deseas eliminar los productos seleccionados de la lista de carga?',
      confirmText: 'Sí, eliminar',
      cancelText: 'Cancelar',
      type: 'danger',
      onConfirm: () => {
        setProducts(prev => {
          const next = prev?.filter(p => !selectedProducts?.includes(p?.id));
          updatePreviewBatch({ products: next, selectedProducts: [] });
          return next;
        });
        setSelectedProducts([]);
        showInfo('Productos eliminados de la lista de carga', { title: 'Eliminación masiva' });
      }
    });
  };

  const handlePreviewBatchSelect = (batchId) => {
    if (isPublishing) return;
    const batch = getBatchById(fileBatchesRef.current, batchId);
    if (!batch) return;

    setEditingProduct(null);
    setShowEditModal(false);
    setPreviewBatchId(batch.id);
    syncViewFromBatch(batch);
  };

  const handleRemoveQueuedBatches = (batchIds) => {
    const ids = Array.isArray(batchIds) ? batchIds : [batchIds];
    const currentBatches = fileBatchesRef.current || [];
    const previewBatchIndex = currentBatches.findIndex(batch => batch?.id === previewBatchId);
    const removableIds = currentBatches
      .filter(batch => ids.includes(batch?.id) && (batch?.status === 'pending' || (!isPublishing && batch?.status === 'active')))
      .map(batch => batch.id);

    if (removableIds.length === 0) return;
    const previewWasRemoved = removableIds.includes(previewBatchId);

    const nextBatches = removeQueuedBatches(currentBatches, removableIds, { isPublishing });
    fileBatchesRef.current = nextBatches;
    setFileBatches(nextBatches);
    setSelectedPendingBatchIds(prev => prev.filter(id => !removableIds.includes(id)));

    const nextActiveBatch = nextBatches.find(batch => batch?.status === 'active') || null;
    if (!nextActiveBatch) {
      setActiveBatchId(null);
      setPreviewBatchId(null);
      setProducts([]);
      setSelectedProducts([]);
      setOmittedInfo({ count: 0, details: [] });
      setValidationResults(null);
      setUploadedFiles([]);
      if (!isPublishing) setCurrentStep('upload');
      return;
    }

    if (nextActiveBatch.id !== activeBatchId) {
      setActiveBatchId(nextActiveBatch.id);
    }

    const nextPreviewBatch = previewWasRemoved
      ? getPreviewFallbackBatch(nextBatches, previewBatchIndex)
      : getBatchById(nextBatches, previewBatchId);
    if (nextPreviewBatch) {
      setPreviewBatchId(nextPreviewBatch.id);
      syncViewFromBatch(nextPreviewBatch);
    }
  };

  const handleStepChange = (stepId) => {
    if (stepId === 'upload' || (stepId === 'preview' && products?.length > 0)) {
      setCurrentStep(stepId);
    }
  };

  const canProceedToPublish = Array.isArray(products) && products.length > 0;

  const previewBatch = getPreviewBatch();
  // MCO_PANEL_ARCHIVOS_REACT_V2
  // MCO_GRID_TRES_COLUMNAS_SEGURO
  // Diseño visual únicamente. Conserva toda la lógica anterior.
  const renderFileQueue = () => {
    if (!Array.isArray(fileBatches) || fileBatches.length <= 1) return null;

    const statusLabel = {
      active: 'Activo',
      pending: 'Pendiente',
      completed: 'Completado'
    };

    return (
      <div className="rounded-[22px] border border-[#dfe7f3] bg-white px-4 py-5 shadow-[0_12px_38px_rgba(30,57,105,0.07)] sm:px-6">

        {/* Encabezado del panel */}
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex min-w-0 items-center gap-4">
            <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-[#e8f1ff] text-[#2563eb]">
              <Icon name="Files" size={28} />
            </div>

            <div className="min-w-0">
              <h3 className="text-[20px] font-bold tracking-tight text-[#142448] sm:text-[22px]">
                Archivos cargados: {fileBatches.length}
              </h3>
              <p className="text-xs leading-5 text-[#7183a1] sm:text-sm">
                Cada archivo conserva su propio lote, selección y categoría.
              </p>
            </div>
          </div>

          {selectedPendingBatchIds.length > 0 && (
            <Button
              variant="outline"
              size="sm"
              iconName="Trash2"
              iconPosition="left"
              onClick={() => handleRemoveQueuedBatches(selectedPendingBatchIds)}
            >
              Eliminar seleccionados
            </Button>
          )}
        </div>

        {/* Lista de tarjetas JSON */}
        <div className="mt-5 grid max-h-[680px] grid-cols-1 content-start gap-2 overflow-y-auto pr-1 md:grid-cols-2 lg:grid-cols-3">
          {fileBatches.map(batch => {
            const canSelectPending = batch?.status === 'pending';
            const canDelete = canSelectPending ||
              (!isPublishing && batch?.status === 'active');

            const isPreviewed = batch?.id === previewBatchId;
            const isActive = batch?.status === 'active';
            const isCompleted = batch?.status === 'completed';
            const isSelected = selectedPendingBatchIds.includes(batch?.id);

            return (
              <div
                key={batch?.id}
                className={`flex h-full min-w-0 items-center gap-2 rounded-xl border px-2 py-2 transition-colors sm:px-3 ${
                  isPreviewed
                    ? 'border-[#5286ff] bg-[#f1f6ff] shadow-[inset_3px_0_0_#2563eb,0_2px_8px_rgba(37,99,235,0.07)]'
                    : isSelected
                      ? 'border-[#a9c4fa] bg-[#f6f9ff]'
                      : 'border-[#e0e7f1] bg-white hover:border-[#bdd0f3] hover:bg-[#fafcff]'
                }`}
              >

                {/* Checkbox original, sin cambiar su comportamiento */}
                <div className="flex w-6 shrink-0 items-center justify-center">
                  {canSelectPending ? (
                    <Checkbox
                      checked={selectedPendingBatchIds.includes(batch?.id)}
                      onChange={(event) => setSelectedPendingBatchIds(prev =>
                        event?.target?.checked
                          ? [...prev, batch.id]
                          : prev.filter(id => id !== batch.id)
                      )}
                    />
                  ) : isActive ? (
                    <span
                      className="flex h-6 w-6 items-center justify-center rounded-md bg-[#2563eb] text-white"
                      aria-hidden="true"
                    >
                      <Icon name="Check" size={16} />
                    </span>
                  ) : (
                    <span className="h-6 w-6" aria-hidden="true" />
                  )}
                </div>

                {/* Icono de documento */}
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[#f1f5fc] text-[#55749d]">
                  <Icon name="FileText" size={24} />
                </div>

                {/* Nombre, cantidad y estados */}
                <div className="flex min-w-0 flex-1 flex-col gap-1.5">

                  {/* Mismo botón original de visualización */}
                  <button
                    type="button"
                    className="min-w-0 flex-1 text-left disabled:cursor-default"
                    onClick={() => handlePreviewBatchSelect(batch?.id)}
                    disabled={isPublishing}
                  >
                    <p
                      className="line-clamp-2 break-words text-[13px] font-semibold leading-5 text-[#172747]"
                      title={batch?.fileName || 'archivo'}
                    >
                      {batch?.fileName || 'archivo'}
                    </p>
                    <p className="text-xs leading-5 text-[#7890b0]">
                      {Array.isArray(batch?.products) ? batch.products.length : 0} producto(s)
                    </p>
                  </button>

                  {/* Estados visuales, tomados del estado real */}
                  <div className="flex min-w-0 flex-wrap items-center gap-1.5">

                    {isActive ? (
                      <span className="inline-flex items-center gap-2 rounded-full bg-[#dcf8ed] px-3 py-1.5 text-xs font-semibold text-[#008365]">
                        <span className="h-2 w-2 rounded-full bg-[#0eaa7c]" />
                        Activo
                      </span>
                    ) : isCompleted ? (
                      <span className="inline-flex items-center gap-1.5 rounded-full bg-[#dcf8ed] px-3 py-1.5 text-xs font-semibold text-[#008365]">
                        <Icon name="CheckCircle2" size={15} />
                        {statusLabel[batch?.status]}
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1.5 rounded-full bg-[#fff1df] px-3 py-1.5 text-xs font-semibold text-[#bd6308]">
                        <Icon name="Clock3" size={15} />
                        {statusLabel[batch?.status] || 'Pendiente'}
                      </span>
                    )}

                    {isPreviewed && (
                      <span className="inline-flex items-center gap-1.5 rounded-full bg-[#e6efff] px-3 py-1.5 text-xs font-semibold text-[#2563eb]">
                        <Icon name="Eye" size={15} />
                        Visualizando
                      </span>
                    )}
                  </div>
                </div>

                {/* Mismo botón original de eliminación */}
                {canDelete && (
                  <Button
                    variant="ghost"
                    size="sm"
                    iconName="X"
                    className="!h-9 !w-9 !min-w-0 !shrink-0 !rounded-lg !bg-[#eff3f9] !p-0 !text-[#52698a] hover:!bg-[#e4ebf6]"
                    onClick={() => handleRemoveQueuedBatches([batch.id])}
                    aria-label={`Eliminar ${batch?.fileName || 'archivo'}`}
                  />
                )}

              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // Cambiar el texto de la interfaz para reflejar el nuevo límite
  const limitText = 'Hasta 10,000 productos';

  // Ajuste de la posición de la ventana emergente para que no interfiera con la barra de navegación
  const modalStyle = {
    position: 'absolute',
    top: '100px', // Ajusta la posición para que esté debajo de la barra de navegación
    left: '50%',
    transform: 'translateX(-50%)',
    zIndex: 1000,
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Sección de descarga masiva desde Amazon eliminada por solicitud del usuario */}
      <div className="pt-10">
        <div className="max-w-7xl mx-auto px-6 py-8">
          {/* AWS Column Preview Modal */}
          {showColumnPreview && selectedAWSColumns?.length > 0 && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-surface rounded-lg p-6 w-full max-w-2xl mx-4 max-h-[80vh] overflow-y-auto" style={modalStyle}>
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <Icon name="Database" className="text-primary" size={24} />
                    <h3 className="text-lg font-semibold text-text-primary">Columnas AWS Seleccionadas</h3>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    iconName="X"
                    onClick={() => setShowColumnPreview(false)}
                  />
                </div>
                
                <p className="text-text-secondary mb-4">
                  Las siguientes columnas fueron seleccionadas desde la descarga de Amazon y se mantendrán disponibles:
                </p>
                
                <div className="grid grid-cols-2 gap-3 mb-6">
                  {selectedAWSColumns?.map((column, index) => (
                    <div key={index} className="flex items-center space-x-2 p-2 bg-primary/5 rounded-lg">
                      <Icon name="Check" className="text-success" size={16} />
                      <span className="text-sm text-text-primary font-medium">{column}</span>
                    </div>
                  ))}
                </div>
                
                <div className="bg-accent/10 border border-accent/20 rounded-lg p-4 mb-4">
                  <div className="flex items-start space-x-2">
                    <Icon name="Info" className="text-accent mt-0.5" size={16} />
                    <div className="text-sm">
                      <p className="font-medium text-accent mb-1">Configuraciones Automáticas Aplicadas:</p>
                      <ul className="text-text-secondary space-y-1">
                        <li>• Disponibilidad configurada a 12 días</li>
                        <li>• Precios optimizados para terminar en 999</li>
                        <li>• Categorías verificadas y corregidas</li>
                        <li>• Ajuste +40% disponible en edición masiva</li>
                      </ul>
                    </div>
                  </div>
                </div>
                
                <Button
                  variant="primary"
                  onClick={() => setShowColumnPreview(false)}
                  className="w-full"
                >
                  Continuar con Carga
                </Button>
              </div>
            </div>
          )}

          {/* Recovery Modal */}
          {showRecoveryModal && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-surface rounded-lg p-6 w-full max-w-md mx-4" style={modalStyle}>
                <div className="flex items-center space-x-3 mb-4">
                  <Icon name="AlertTriangle" className="text-warning" size={24} />
                  <h3 className="text-lg font-semibold text-text-primary">Datos de Sesión Encontrados</h3>
                </div>
                
                <p className="text-text-secondary mb-6">
                  Se encontraron datos de una sesión anterior incompleta. ¿Deseas recuperar tu progreso?
                </p>
                
                {recoveryData?.session && (
                  <div className="mb-4 p-3 bg-muted rounded-lg text-sm">
                    <p><strong>Productos:</strong> {recoveryData?.session?.products?.length || 0}</p>
                    <p><strong>Paso:</strong> {recoveryData?.session?.currentStep}</p>
                    <p><strong>Guardado:</strong> {new Date(recoveryData.session.timestamp)?.toLocaleString('es-ES')}</p>
                  </div>
                )}
                
                <div className="flex space-x-3">
                  <Button
                    variant="default"
                    onClick={handleRecoverData}
                    className="flex-1"
                  >
                    Recuperar Datos
                  </Button>
                  <Button
                    variant="outline"
                    onClick={handleDiscardRecovery}
                    className="flex-1"
                  >
                    Empezar de Nuevo
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Validation Details Modal */}
          {showValidationModal && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-surface rounded-lg p-6 w-full max-w-3xl mx-4 max-h-[85vh] overflow-y-auto" style={modalStyle}>
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <Icon name="ListChecks" className="text-accent" size={20} />
                    <h3 className="text-lg font-semibold text-text-primary">
                      Detalles de Validación{validationModalCategory && validationModalCategory !== 'all' ? ` • ${validationModalCategory}` : ''}
                    </h3>
                  </div>
                  <Button variant="ghost" size="sm" iconName="X" onClick={handleCloseValidationDetails} />
                </div>

                {validationResults?.details?.length ? (
                  <div className="space-y-3">
                    {validationResults?.details
                      ?.filter(d => !validationModalCategory || validationModalCategory === 'all' || d?.field === validationModalCategory)
                      ?.slice(0, 200)
                      ?.map((issue, idx) => (
                        <div
                          key={idx}
                          className={`p-3 rounded border ${
                            issue?.severity === 'error'
                              ? 'border-error/20 bg-error/5 dark:border-error/40 dark:bg-error/15'
                              : 'border-warning/20 bg-warning/5 dark:border-warning/40 dark:bg-warning/15'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-2">
                              <Icon name={issue?.severity === 'error' ? 'AlertCircle' : 'AlertTriangle'} size={14} className={issue?.severity === 'error' ? 'text-error' : 'text-warning'} />
                              <span className="text-sm font-medium text-text-primary capitalize">{issue?.field}</span>
                            </div>
                            <span className={`text-xs ${issue?.severity === 'error' ? 'text-error' : 'text-warning'}`}>{issue?.severity}</span>
                          </div>
                          <div className="mt-1 text-sm text-text-secondary">{issue?.message}</div>
                        </div>
                      ))}
                    {validationResults?.details?.length > 200 && (
                      <div className="text-xs text-text-secondary text-center">Mostrando 200 más recientes…</div>
                    )}
                  </div>
                ) : (
                  <div className="text-sm text-text-secondary">No hay detalles de validación para mostrar.</div>
                )}

                <div className="mt-6 flex justify-end">
                  <Button variant="default" onClick={handleCloseValidationDetails}>Cerrar</Button>
                </div>
              </div>
            </div>
          )}

          {/* Omitted Products Details Modal */}
          {showOmittedModal && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-surface rounded-lg p-6 w-full max-w-3xl mx-4 max-h-[85vh] overflow-y-auto" style={modalStyle}>
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <Icon name="AlertTriangle" className="text-warning" size={20} />
                    <h3 className="text-lg font-semibold text-text-primary">
                      Productos omitidos por palabras prohibidas
                    </h3>
                  </div>
                  <Button variant="ghost" size="sm" iconName="X" onClick={() => setShowOmittedModal(false)} />
                </div>

                <div className="bg-warning/10 border border-warning/20 rounded-lg p-4 mb-4">
                  <p className="text-sm text-text-secondary">
                    Por cumplimiento, se omitieron {omittedInfo?.count || 0} productos que contienen palabras o marcas prohibidas. Estos productos no se mostrarán ni se publicarán.
                  </p>
                </div>

                {omittedInfo?.details?.length ? (
                  <div className="space-y-3">
                    {omittedInfo?.details?.slice(0, 300)?.map((item, idx) => (
                      <div key={idx} className="p-3 rounded border border-border">
                        <div className="text-sm font-medium text-text-primary truncate">{item?.product || `Producto ${idx + 1}`}</div>
                        {Array.isArray(item?.matches) && item.matches.length > 0 && (
                          <div className="text-xs text-text-secondary mt-1">
                            Coincidencias: {item.matches.join(', ')}
                          </div>
                        )}
                      </div>
                    ))}
                    {omittedInfo?.details?.length > 300 && (
                      <div className="text-xs text-text-secondary text-center">Mostrando 300 de {omittedInfo?.details?.length} productos omitidos…</div>
                    )}
                  </div>
                ) : (
                  <div className="text-sm text-text-secondary">No hay detalles para mostrar.</div>
                )}

                <div className="mt-6 flex justify-end gap-2">
                  <Button variant="outline" iconName="Download" iconPosition="left" onClick={downloadOmittedReport}>
                    Descargar reporte
                  </Button>
                  <Button variant="default" onClick={() => setShowOmittedModal(false)}>Cerrar</Button>
                </div>
              </div>
            </div>
          )}

          {/* Amazon Column Selector Modal */}
          {showColumnSelector && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-surface rounded-lg p-6 w-full max-w-6xl mx-4 max-h-[90vh] overflow-y-auto" style={modalStyle}>
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center space-x-3">
                    <Icon name="Database" className="text-primary" size={24} />
                    <div>
                      <h3 className="text-lg font-semibold text-text-primary">
                        Configuración de las {allColumns?.length} Columnas Específicas
                      </h3>
                      <p className="text-sm text-text-secondary">
                        Estas son las columnas exactas de tu archivo Excel para carga y descarga
                      </p>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    iconName="X"
                    onClick={() => setShowColumnSelector(false)}
                  />
                </div>

                <div className="bg-accent/10 border border-accent/20 rounded-lg p-4 mb-6">
                  <div className="flex items-start space-x-2">
                    <Icon name="Info" className="text-accent mt-0.5" size={16} />
                    <div className="text-sm">
                      <p className="font-medium text-accent mb-1">Columnas Configuradas Automáticamente:</p>
                      <p className="text-text-secondary">
                        Las {allColumns?.length} columnas específicas están preconfiguradas según tu archivo Excel.
                        Todas están seleccionadas por defecto para asegurar compatibilidad completa.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center space-x-4">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleSelectAllColumns(true)}
                    >
                      Seleccionar Todas ({allColumns?.length})
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleSelectAllColumns(false)}
                    >
                      Deseleccionar Todas
                    </Button>
                  </div>
                  <div className="text-sm text-text-secondary">
                    {Object?.values(selectedColumns)?.filter(Boolean)?.length} de {allColumns?.length} columnas seleccionadas
                  </div>
                </div>

                {/* Grid de selección de columnas - mostrando todas las columnas configuradas */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 max-h-96 overflow-y-auto border border-border rounded-lg p-4">
                  {allColumns?.map((column, index) => (
                    <div
                      key={column?.id}
                      className={`flex items-center space-x-3 p-3 rounded-lg border transition-all ${
                        selectedColumns?.[column?.id]
                          ? 'bg-primary/5 border-primary/20' : 'bg-background border-border hover:bg-muted/50'
                      }`}
                    >
                      <Checkbox
                        checked={selectedColumns?.[column?.id] || false}
                        onChange={(e) => handleColumnChange(column?.id, e?.target?.checked)}
                        className="flex-shrink-0"
                      />
                      <div className="flex items-center justify-between w-full">
                        <span className={`text-sm font-medium ${
                          selectedColumns?.[column?.id] ? 'text-primary' : 'text-text-primary'
                        }`}>
                          {column?.label}
                        </span>
                        <span className="text-xs text-text-secondary bg-muted px-2 py-1 rounded">
                          #{index + 1}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="flex justify-between items-center mt-8 pt-4 border-t border-border">
                  <div className="text-sm text-text-secondary">
                    Configuración de las {allColumns?.length} columnas específicas aplicada a procesamiento y plantillas
                  </div>
                  <div className="flex space-x-3">
                    <Button
                      variant="default"
                      onClick={() => {
                        setShowColumnSelector(false);
                        const selectedCount = Object?.values(selectedColumns)?.filter(Boolean)?.length;
                        showSuccess(`Configuración de ${selectedCount} columnas guardada correctamente`, {
                          title: `${allColumns?.length} Columnas Configuradas`,
                          duration: 5000
                        });
                      }}
                    >
                      Guardar Configuración
                    </Button>
                    <Button
                      variant="outline"
                      onClick={handleDownloadTemplate}
                      iconName="Download"
                      iconPosition="left"
                    >
                      Descargar Plantilla ({allColumns?.length} Cols)
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Page Header */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center">
                  <Icon name="Upload" size={20} color="white" />
                </div>
                <div>
                  <h1 className="text-2xl font-semibold text-text-primary">
                    Carga Masiva de Productos
                  </h1>
                  <p className="text-text-secondary">
                    Importa y publica múltiples productos a MercadoLibre
                  </p>
                </div>
              </div>
              {false && (
<Button
                variant="outline"
                size="sm"
                iconName="Settings"
                iconPosition="left"
                onClick={() => setShowColumnSelector(true)}
              >
                Configurar {allColumns?.length} Columnas
              </Button>
)}
            </div>

            {/* Progress Steps */}
            <div className="flex items-center space-x-4 overflow-x-auto pb-2">
              {steps?.map((step, index) => {
                const isActive = step?.id === currentStep;
                const isCompleted = steps?.findIndex(s => s?.id === currentStep) > index;
                const isClickable = step?.id === 'upload' || (step?.id === 'preview' && products?.length > 0);

                return (
                  <button
                    key={step?.id}
                    onClick={() => isClickable && handleStepChange(step?.id)}
                    className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-all whitespace-nowrap ${
                      isActive
                        ? 'bg-primary text-primary-foreground'
                        : isCompleted
                        ? 'bg-success/10 text-success hover:bg-success/20'
                        : isClickable
                        ? 'bg-muted text-text-secondary hover:bg-muted/80' : 'bg-muted/50 text-muted-foreground cursor-not-allowed'
                    }`}
                    disabled={!isClickable}
                  >
                    <Icon 
                      name={isCompleted ? "CheckCircle" : step?.icon} 
                      size={16} 
                    />
                    <span className="font-medium">{step?.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Content */}
          <div className="space-y-8">
            {currentStep === 'upload' && (
              <>
                <FileUploadZone
                  onFileSelect={handleFileSelect}
                  isProcessing={isProcessing}
                  acceptedFiles={uploadedFiles}
                />
              </>
            )}

            {currentStep === 'preview' && products?.length > 0 && (
              <>
                {renderFileQueue()}

                {omittedInfo?.count > 0 && (
                  <div className="p-4 bg-warning/10 border border-warning/20 rounded-lg">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start space-x-2">
                        <Icon name="AlertTriangle" className="text-warning mt-0.5" size={18} />
                        <div>
                          <p className="text-sm font-medium text-text-primary">
                            Se omitieron {omittedInfo?.count} producto{omittedInfo?.count !== 1 ? 's' : ''} por contener palabras o marcas prohibidas.
                          </p>
                          <p className="text-xs text-text-secondary">Estos productos no se muestran ni se publican.</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button variant="outline" size="sm" iconName="ListChecks" iconPosition="left" onClick={() => setShowOmittedModal(true)}>
                          Ver detalles
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => setOmittedInfo({ count: 0, details: [] })}>Ocultar</Button>
                      </div>
                    </div>
                  </div>
                )}
                {validationResults && (
                  <ValidationSummary
                    validationResults={validationResults}
                    onViewDetails={handleOpenValidationDetails}
                    onFixErrors={() => setCurrentStep('preview')}
                  />
                )}

                <ProductPreviewTable
                  products={products}
                  selectedProducts={selectedProducts}
                  onBulkSelect={handleBulkSelect}
                  onProductUpdate={handleProductUpdate}
                  onProductDelete={handleRequestDeleteProduct}
                  onValidationUpdate={performValidation}
                  selectedColumns={selectedColumns} // Pass selectedColumns
                  onErrorAnalysis={handleErrorAnalysis} // Pass error analysis handler
                  allColumns={allColumns} // Pass allColumns for reference
                />

                {/* Product Edit Modal */}
                {showEditModal && editingProduct && (
                  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-surface rounded-lg p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-semibold text-text-primary">Editar Producto</h3>
                        <Button
                          variant="ghost"
                          size="sm"
                          iconName="X"
                          onClick={() => setShowEditModal(false)}
                        />
                      </div>
                      
                      <div className="space-y-4">
                        <Input
                          label="Título"
                          value={editingProduct?.title || ''}
                          onChange={(e) => setEditingProduct(prev => ({
                            ...prev,
                            title: e?.target?.value
                          }))}
                          placeholder="Título del producto"
                        />
                        
                        <Input
                          label="Precio"
                          type="number"
                          value={editingProduct?.price ?? ''}
                          onChange={(e) => setEditingProduct(prev => ({
                            ...prev,
                            price: e?.target?.value ?? ''
                          }))}
                          placeholder="Precio en pesos"
                        />
                        
                        <Input
                          label="Categoría"
                          value={editingProduct?.category || ''}
                          onChange={(e) => setEditingProduct(prev => ({
                            ...prev,
                            category: e?.target?.value
                          }))}
                          placeholder="ID de categoría MLA"
                        />
                        
                        <div className="space-y-2">
                          <label className="block text-sm font-medium text-text-primary">
                            Descripción
                          </label>
                          <textarea
                            className="w-full p-3 border border-border rounded-lg resize-none bg-background text-text-primary"
                            rows={4}
                            value={editingProduct?.description || ''}
                            onChange={(e) => setEditingProduct(prev => ({
                              ...prev,
                              description: e?.target?.value
                            }))}
                            placeholder="Descripción del producto"
                          />
                        </div>
                        
                        <Input
                          label="Stock"
                          type="number"
                          value={editingProduct?.stock || ''}
                          onChange={(e) => setEditingProduct(prev => ({
                            ...prev,
                            stock: parseInt(e?.target?.value) || 0
                          }))}
                          placeholder="Cantidad en stock"
                        />
                      </div>
                      
                      <div className="flex space-x-3 mt-6">
                        <Button
                          variant="default"
                          onClick={() => handleSaveProduct(editingProduct)}
                        >
                          Guardar Cambios
                        </Button>
                        <Button
                          variant="outline"
                          onClick={() => setShowEditModal(false)}
                        >
                          Cancelar
                        </Button>
                      </div>
                    </div>
                  </div>
                )}

                <BulkEditPanel
                  attributeExportBatches={fileBatches}
                  selectedCount={selectedProducts?.length}
                  onBulkUpdate={handleBulkUpdate}
                  onClearSelection={() => {
                    setSelectedProducts([]);
                    updatePreviewBatch({ selectedProducts: [] });
                  }}
                  isVisible={selectedProducts?.length > 0}
                  sourceFileName={previewBatch?.fileName || ''}
                  batchCategoryId={previewBatch?.categoryOverride || previewBatch?.detectedCategoryId || previewBatch?.detectedCategory || previewBatch?.products?.find(product => product?.category_id)?.category_id || ''}
                  batchCategoryPath={previewBatch?.appliedCategoryPath || previewBatch?.categoryPath || ''}
                  selectedProducts={selectedProductsForBulkEdit}
                  usdToCopRateStatus={usdToCopRateStatus}
                  formattedUsdToCopRate={formattedUsdToCopRate}
                  formattedUsdToCopFetchedAt={formattedUsdToCopFetchedAt}
                />

                {/* Show bulk actions bar when products are selected */}
                {selectedProducts?.length > 0 && (
                  <div className="sticky bottom-4 z-10">
                    <div className="mx-auto max-w-lg rounded-2xl border border-border bg-surface/95 p-4 shadow-[0_12px_28px_rgba(15,23,42,0.18)] backdrop-blur-sm">
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-2 text-text-primary">
                          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10 text-primary">
                            <Icon name="CheckSquare" size={18} />
                          </div>
                          <span className="font-medium text-sm">
                            {selectedProducts?.length} producto{selectedProducts?.length !== 1 ? 's' : ''} seleccionado{selectedProducts?.length !== 1 ? 's' : ''}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="secondary"
                            size="sm"
                            iconName="Trash2"
                            onClick={handleDeleteSelected}
                          >
                            Eliminar
                          </Button>
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => {
                              setSelectedProducts([]);
                              updatePreviewBatch({ selectedProducts: [] });
                            }}
                          >
                            Limpiar
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                <div className="flex justify-between">
                  <Button
                    variant="outline"
                    iconName="ArrowLeft"
                    iconPosition="left"
                    onClick={() => setCurrentStep('upload')}
                  >
                    Volver a Cargar
                  </Button>
                  
                  <Button
                    variant="default"
                    iconName="ArrowRight"
                    iconPosition="right"
                    onClick={() => setCurrentStep('publish')}
                    disabled={!canProceedToPublish}
                  >
                    Continuar a Publicación
                  </Button>
                </div>
              </>
            )}

            {currentStep === 'publish' && (
              <>
                {renderFileQueue()}

                <PublishingPanel
                  selectedProducts={selectedProducts}
                  totalProducts={products?.length}
                  onStartPublishing={handleStartPublishing}
                  onPausePublishing={handlePausePublishing}
                  onResumePublishing={handleResumePublishing}
                  onCancelPublishing={handleCancelPublishing}
                  isPublishing={isPublishing}
                  publishingProgress={publishingProgress}
                  onRetryProduct={(productId) => retryFailedProducts([productId])}
                  onRetryFailed={() => retryFailedProducts()}
                  onDownloadErrorReport={downloadErrorReport}
                />

                {!isPublishing && (
                  <div className="flex justify-between">
                    <Button
                      variant="outline"
                      iconName="ArrowLeft"
                      iconPosition="left"
                      onClick={() => setCurrentStep('preview')}
                    >
                      Volver a Vista Previa
                    </Button>
                    
                    <Button
                      variant="outline"
                      iconName="History"
                      iconPosition="left"
                      onClick={() => navigate('/upload-history')}
                    >
                      Ver Historial
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Acciones rápidas (sin publicidad) */}
          <div className="mt-12 p-4 bg-surface rounded-lg border border-border">
            <div className="flex items-center space-x-3">
              <Button
                variant="outline"
                size="sm"
                iconName="Settings"
                iconPosition="left"
                onClick={() => navigate('/api-configuration')}
              >
                Configurar API MercadoLibre
              </Button>
              {false && (
<Button
                variant="outline"
                size="sm"
                iconName="Download"
                iconPosition="left"
                onClick={handleDownloadTemplate}
              >
                Descargar Plantilla ({allColumns?.length} Columnas)
              </Button>
)}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BulkUpload;